import { useWalletBackgroundRefresh } from "./useWalletBackgroundRefresh";
import { paymentLinkNetworkError } from "../helpers/paymentLink";
import { recoveryErrorMessage } from "../../utils/recovery-error";
import { decodePrivateBridge } from "../../joinsplit-v2/privateBridgeConfig";
import { decodePrivateSwap } from "../../joinsplit-v2/privateSwapConfig";
import {
  privatePools,
  privatePoolDeploymentBlock,
  privateTokenPool,
  PRIVATE_TOKEN_RELAY_INTERFACE,
} from "../../joinsplit-v2/privatePools";
import { requestPrivateFees } from "../../utils/relayer-client";
import { DEPOSIT_EVIDENCE_INTERFACE } from "../../joinsplit-v2/depositEvidence";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type React from "react";
import { ethers } from "ethers";
import { amountValidation, isPaymentToken } from "../helpers/amountValidation";
import { readWalletTokenBalance } from "./useWalletTokenBalance";
import { friendlyError } from "../utils/errors";
import { mergeMigrationNotes } from "../helpers/migrateRetiredBalance";
import { flushSync } from "react-dom";
import { EVM_CONFIG, type TokenInfo } from "../../config";
import { getBaseReadProvider } from "../../utils/base-read-provider";
import { configuredEvmRecoveryRegistry } from "../../joinsplit-v2/onchainRecoveryEvm";
import { scanEvmOnchainRecovery } from "../../joinsplit-v2/onchainRecoveryEvmScan";
import { getDirectEvmRecoveryProvider } from "../../joinsplit-v2/recoveryDirectRpc";
import type { BaseStoredNote } from "../types";
import { hasActiveFlatJoinSplitV2Operation } from "../../joinsplit-v2/walletLock";
import { startRecoveryPolling } from "../helpers/recoveryPolling";
import { refreshPrivateState as runPrivateStateRefresh } from "../helpers/refreshPrivateState";
import {
  assertPrivateStorageAccess,
  storePrivateCommitment,
  storePrivateHistoryEntry,
  storePrivateInboxNote,
} from "../../api/private-data-storage";
import { submitBridgeQuote } from "../../utils/relayer-client";
import { formatMpkHex, hashMpkHex } from "../../utils/format";
import { deriveRecoveryKeyFromRecipientKey } from "../../utils/recovery";
import {
  encryptInboxNoteWithPublicKey,
  formatInboxPublicKeyHex,
  parsePrivateAddress,
} from "../../utils/inbox-crypto";
import { isStealthHandle } from "../../utils/handle-resolver";
import { usesPrivateFees } from "../../joinsplit-v2/privateFees";
import { flatRelayFee } from "../../joinsplit-v2/relayFee";
import { PREVIOUS_POOL_MODE } from "../../config";
import { usePrivateFeePolicy } from "./usePrivateFeePolicy";
import { useComplianceToast } from "./useComplianceToast";
import { usePerpsTabController } from "./usePerpsTabController";
import {
  BRIDGE_DESTINATION_CHAIN_ID,
  buildEngineUrl,
  parseMpk,
} from "../helpers/baseAppUtils";
import {
  buildBridgeQuoteIdempotencyKey,
  withBridgeQuoteMetadata,
} from "./useBaseBridgeQuote";
import {
  type PendingInboxNote,
  readEncryptedPendingInboxNotes,
  upsertEncryptedPendingInboxNote,
  writeEncryptedPendingInboxNotes,
} from "../helpers/pendingInboxNotes";
import { parseBatchRecipientFile } from "../utils/batchFileParser";
import {
  markFlatJoinSplitV2SettlementDeliveryCompleted,
  markFlatJoinSplitV2SettlementDeliveryPending,
  reconcileFlatJoinSplitV2SettlementJournal,
  removeFlatJoinSplitV2SettlementJournalRecord,
  stageFlatJoinSplitV2SettlementDeliveryEnvelope,
  type FlatJoinSplitV2JournalScope,
} from "../../joinsplit-v2";

/** The fee quote a settlement was authorized under. Token-relay settlements
 * carry it in their calldata; bridges and swaps settle through their
 * coordinators, so the same nonce sits inside the decoded leg. */
function settlementQuoteId(record: {
  kind: string;
  transaction: { data: string };
}): string | undefined {
  try {
    const parsed = (
      record.kind === "deposit"
        ? DEPOSIT_EVIDENCE_INTERFACE
        : PRIVATE_TOKEN_RELAY_INTERFACE
    ).parseTransaction({ data: record.transaction.data });
    const id =
      parsed?.name === "settle"
        ? parsed.args.tokenLeg.quote.nonce
        : parsed?.args.quote?.nonce;
    if (id) return String(id);
  } catch {
    /* not a token relay settlement */
  }
  const bridge = decodePrivateBridge(record.transaction.data);
  if (bridge?.leg?.quote?.nonce) return String(bridge.leg.quote.nonce);
  const swap = decodePrivateSwap(record.transaction.data);
  if (swap?.leg?.quote?.nonce) return String(swap.leg.quote.nonce);
  return undefined;
}

export function useBaseAppActions(params: any) {
  const {
    NOTES_API_URL,
    activeDepositToken,
    activeSendToken,
    addLog,
    approveTokenSpend,
    authHeadersForPath,
    baseCacheSecret,
    chainId,
    configuredPerpsAssetToken,
    crossChainAmount,
    crossChainDestinationChain,
    crossChainDestinationTokenAddress,
    crossChainExecutionDestinationAuthority,
    crossChainExecutionDestinationRecipient,
    crossChainDestinationResolutionError,
    crossChainDestinationResolutionLoading,
    crossChainInputToken,
    crossChainOrderStatus,
    crossChainPublicRecipientAddress,
    crossChainQuote,
    crossChainQuoteError,
    crossChainQuoteRequestKey,
    crossChainRecipientMode,
    crossChainSourceAmountBase,
    clearProxyAuthToken,
    depositAmount,
    walletTokenBalance,
    spendablePrivateBalance,
    depositMaxQuote,
    depositRecipient,
    disconnectWallet,
    enableRangeScreening,
    ensureProxyAuthToken,
    fetchTokenBalance,
    fetchTokenPrices,
    fetchWalletBalance,
    handleBatchWithdraw,
    handleBridge,
    handleBridgeCancel,
    handleSend,
    handleShield,
    setDepositProgress,
    handleSwap,
    isPaymentMode,
    paymentLinkAmountInput,
    paymentLinkToken,
    prevWalletConnectedRef,
    pushHistoryEntry,
    screeningError,
    screeningStatus,
    sendAmount,
    sendBatchMode,
    sendBatchRows,
    sendMemo,
    sendRecipient,
    setBalancesRefreshing,
    setCrossChainOrderStatus,
    setCrossChainQuote,
    setCrossChainQuoteError,
    setCrossChainQuoteLoading,
    setDepositAmount,
    setDepositRecipient,
    setHistoryError,
    setHistoryLoading,
    setLastPaymentTxHash,
    setLoading,
    setNotes,
    setPaymentLinkAmountInput,
    setPaymentLinkToken,
    setPendingSpentCommitments,
    setSendAmount,
    setSendBatchMode,
    setSendBatchRows,
    setSendMemo,
    setSendRecipient,
    setStatus,
    setSwapAmount,
    setSwapQuote,
    setSwapQuoteError,
    setWalletModalOpen,
    storeHistoryEvent,
    swapAmount,
    swapInputToken,
    swapOutputToken,
    swapQuote,
    swapSlippageBps,
    syncHistoryEntries,
    syncInboxNotes,
    prepareInboxSnapshot,
    checkSyncHead,
    walletSyncScope,
    proxyAuthReady,
    resolveUnindexedNotes,
    resolveNotesAfterUnchangedHead,
    wallet,
  } = params;
  const lastRefreshSkippedRef = useRef(false);
  const [chainRecovery, setChainRecovery] = useState<{
    scope: unknown;
    state: "loading" | "ready" | "error";
  } | null>(null);
  const refreshTimingsRef = useRef<
    Record<string, { durationMs: number; succeeded: boolean }>
  >({});
  /** Whether the journal check of the current refresh has finished, and how.
   * A refresh no longer waits for it, so "not finished yet" must not be
   * reported as unresolved records. */
  const journalCheckRef = useRef<{ settled: boolean; complete: boolean }>({
    settled: false,
    complete: false,
  });
  const currentWalletRef = useRef(wallet);
  currentWalletRef.current = wallet;
  const flatSettlementFlushRef = useRef<Promise<boolean> | null>(null);
  const balancesRefreshRef = useRef<Promise<void> | null>(null);
  const refreshScopeRef = useRef(walletSyncScope);
  if (refreshScopeRef.current !== walletSyncScope) {
    refreshScopeRef.current = walletSyncScope;
    balancesRefreshRef.current = null;
    flatSettlementFlushRef.current = null;
  }
  const pendingInboxRetryTimersRef = useRef<Set<number>>(new Set());

  const waitForNextPaint = useCallback(async () => {
    if (typeof window === "undefined") return;
    await new Promise<void>((resolve) => {
      window.requestAnimationFrame(() => resolve());
    });
  }, []);

  const isDlnQuoteNotAcceptedError = (message?: string | null) => {
    if (!message) return false;
    return (
      message.includes("DLN_QUOTE_NOT_ACCEPTED") ||
      /quote (was )?not accepted/i.test(message)
    );
  };

  const isConfirmedTxHash = (value?: string | null) =>
    typeof value === "string" && /^0x[0-9a-fA-F]{64}$/.test(value);

  const isCommitmentNotIndexedError = (message?: string | null) =>
    /commitment is not indexed yet/i.test(message || "");

  const refreshPrivateState = useCallback(
    async (recoverJournal?: () => Promise<boolean>) => {
      const registry = configuredEvmRecoveryRegistry(EVM_CONFIG.CHAIN_ID);
      if (registry) {
        lastRefreshSkippedRef.current = false;
        const currentWallet = currentWalletRef.current;
        const scope = refreshScopeRef.current;
        if (!currentWallet.connected || !currentWallet.spendingKeys) return;
        const isCurrent = () =>
          currentWalletRef.current.spendingKeys ===
            currentWallet.spendingKeys && refreshScopeRef.current === scope;
        setChainRecovery({ scope, state: "loading" });
        addLog(
          "Reading encrypted recovery data directly from the blockchain",
          "info",
        );
        const result = await scanEvmOnchainRecovery({
          provider: getDirectEvmRecoveryProvider(EVM_CONFIG.CHAIN_ID),
          registry,
          spendingKeys: currentWallet.spendingKeys,
          pools: privatePools().map((address) => ({
            address,
            deploymentBlock: privatePoolDeploymentBlock(address),
          })),
          isCurrent,
        }).catch((error) => {
          if (isCurrent()) setChainRecovery({ scope, state: "error" });
          throw error;
        });
        if (!isCurrent()) return;
        const identity = (stored: BaseStoredNote) =>
          `${stored.note.poolAddress?.toLowerCase()}:${stored.note.commitment.toLowerCase()}`;
        const verified = new Map(
          result.notes.map((stored) => [identity(stored), stored]),
        );
        setNotes((previous: BaseStoredNote[]) => {
          if (!isCurrent()) return previous;
          const next = new Map(
            previous.map((stored) => [
              identity(stored),
              {
                ...stored,
                note: { ...stored.note, leafIndex: -1 },
              },
            ]),
          );
          for (const [id, stored] of verified) next.set(id, stored);
          return [...next.values()];
        });
        const available = result.notes.filter((stored) => !stored.spent).length;
        setChainRecovery({ scope, state: "ready" });
        addLog(
          `On-chain recovery complete: ${available} available private note${available === 1 ? "" : "s"}; ${result.pending.length} pending; ${result.notes.length - available} spent. No recovery service was used.`,
          "success",
        );
        return true;
      }
      return runPrivateStateRefresh({
        prepareInbox: prepareInboxSnapshot,
        recoverJournal,
        syncInbox: syncInboxNotes,
        resolveNotes: resolveUnindexedNotes,
        resolveUnchanged: resolveNotesAfterUnchangedHead,
        refreshHistory: syncHistoryEntries,
        waitForAuxiliary: false,
        onAuxiliaryError: () =>
          addLog(
            "Activity or public balance refresh was delayed; private balance validation runs independently.",
            "warning",
          ),
        onRecoveryError: (error: unknown) =>
          addLog(
            `Settlement journal recovery delayed: ${error instanceof Error ? error.message : String(error)}`,
            "warning",
          ),
        checkHead: checkSyncHead,
        onHeadResult: (unchanged: boolean) => {
          lastRefreshSkippedRef.current = unchanged;
        },
        onHeadError: (error: unknown) =>
          addLog(
            `Sync check unavailable (${error instanceof Error ? error.message : String(error)}); validating in full`,
            "warning",
          ),
        onLateRecovery: (complete: boolean) => {
          addLog(
            complete
              ? "Settlement journal check finished: every record is closed."
              : "Settlement journal check finished: records remain open; see the lines above for each one.",
            "info",
          );
          void resolveUnindexedNotes().catch(() => undefined);
        },
        onTiming: (stage, durationMs, succeeded) => {
          refreshTimingsRef.current[stage] = { durationMs, succeeded };
          console.debug("[wallet-refresh]", { stage, durationMs, succeeded });
        },
        refreshPublic: () =>
          Promise.all([
            fetchWalletBalance(),
            activeDepositToken
              ? fetchTokenBalance(activeDepositToken)
              : Promise.resolve(),
            fetchTokenPrices(),
          ]),
      });
    },
    [
      prepareInboxSnapshot,
      checkSyncHead,
      syncInboxNotes,
      resolveUnindexedNotes,
      resolveNotesAfterUnchangedHead,
      syncHistoryEntries,
      fetchWalletBalance,
      fetchTokenBalance,
      activeDepositToken,
      fetchTokenPrices,
      addLog,
      setNotes,
    ],
  );

  const flushPendingInboxNotes = useCallback(async () => {
    if (!wallet.address) return;
    const scheduleRetry = (delayMs = 30_000) => {
      if (typeof window !== "undefined") {
        const timer = window.setTimeout(() => {
          pendingInboxRetryTimersRef.current.delete(timer);
          void flushPendingInboxNotes().catch((error) => {
            addLog(
              `Pending private note retry failed: ${
                error instanceof Error ? error.message : String(error)
              }`,
              "warning",
            );
          });
        }, delayMs);
        pendingInboxRetryTimersRef.current.add(timer);
      }
    };
    if (!baseCacheSecret) {
      scheduleRetry();
      return;
    }
    const pending = await readEncryptedPendingInboxNotes(
      wallet.address,
      baseCacheSecret,
    );
    if (pending.length === 0) return;

    const headers = await authHeadersForPath("/api/v1/notes/store");
    if (!headers.Authorization) {
      scheduleRetry();
      return;
    }

    const remaining: PendingInboxNote[] = [];
    for (const item of pending) {
      try {
        await storePrivateInboxNote({
          baseUrl: NOTES_API_URL,
          recipientKey: item.recipientKey,
          headers,
          encryptedNote: item.encryptedNote,
          commitment: item.commitment,
          treeIndex: item.treeIndex,
        });
        addLog(
          "Pending private note storage completed",
          "success",
          item.txHash,
        );
      } catch (error) {
        const msg = error instanceof Error ? error.message : String(error);
        if (
          isCommitmentNotIndexedError(msg) &&
          !isConfirmedTxHash(item.txHash)
        ) {
          addLog(
            "Dropped unconfirmed pending private note retry",
            "warning",
            item.txHash,
          );
          continue;
        }
        remaining.push({
          ...item,
          attempts: item.attempts + 1,
          lastError: msg,
        });
      }
    }
    await writeEncryptedPendingInboxNotes(
      wallet.address,
      remaining,
      baseCacheSecret,
    );
    if (remaining.length > 0) {
      scheduleRetry(60_000);
    }
  }, [
    NOTES_API_URL,
    addLog,
    authHeadersForPath,
    baseCacheSecret,
    wallet.address,
  ]);

  useEffect(() => {
    void flushPendingInboxNotes().catch((error) => {
      addLog(
        `Pending private note retry failed: ${
          error instanceof Error ? error.message : String(error)
        }`,
        "warning",
      );
    });
    return () => {
      for (const timer of pendingInboxRetryTimersRef.current) {
        window.clearTimeout(timer);
      }
      pendingInboxRetryTimersRef.current.clear();
    };
  }, [addLog, flushPendingInboxNotes]);

  const rememberLocalNote = useCallback(
    (note: any, txHash?: string) => {
      const commitment = String(note?.commitment || "").toLowerCase();
      if (!commitment) return;
      setNotes((prev: any[]) => {
        if (refreshScopeRef.current !== walletSyncScope) return prev;
        if (
          prev.some(
            (entry) =>
              String(entry?.note?.commitment || "").toLowerCase() ===
              commitment,
          )
        ) {
          return prev;
        }
        return [...prev, { note, timestamp: Date.now(), txHash }];
      });
    },
    [setNotes, walletSyncScope],
  );

  const persistInboxNoteOrQueue = useCallback(
    async (params: {
      label: string;
      note: any;
      recipientKey: string;
      expectedAuthRecipientKey?: string;
      inboxPublicKeyHex: string;
      txHash?: string;
      keepLocal?: boolean;
    }) => {
      let encrypted: PendingInboxNote["encryptedNote"] | null = null;
      const commitment = String(params.note?.commitment || "");
      try {
        encrypted = await encryptInboxNoteWithPublicKey(
          params.note,
          params.inboxPublicKeyHex,
        );
        const authHeaders = await authHeadersForPath(
          "/api/v1/notes/store",
          params.expectedAuthRecipientKey
            ? {
                interactive: true,
                expectedRecipientKey: params.expectedAuthRecipientKey,
              }
            : { interactive: true },
        );
        await storePrivateInboxNote({
          baseUrl: NOTES_API_URL,
          recipientKey: params.recipientKey,
          headers: authHeaders,
          encryptedNote: encrypted,
          commitment,
          treeIndex:
            Number.isSafeInteger(params.note?.treeIndex) &&
            params.note.treeIndex >= 0
              ? params.note.treeIndex
              : undefined,
        });
        return true;
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        if (params.keepLocal) {
          rememberLocalNote(params.note, params.txHash);
        }
        if (
          isCommitmentNotIndexedError(msg) &&
          !isConfirmedTxHash(params.txHash)
        ) {
          addLog(
            `${params.label} inbox storage was not queued because the transaction is unconfirmed`,
            "warning",
            params.txHash,
          );
          return false;
        }
        if (!encrypted || !wallet.address || !commitment) {
          addLog(
            `${params.label} inbox store failed: ${msg}`,
            "warning",
            params.txHash,
          );
          return false;
        }
        try {
          if (!baseCacheSecret) {
            throw new Error("Encrypted retry cache is unavailable");
          }
          await upsertEncryptedPendingInboxNote(
            wallet.address,
            {
              recipientKey: params.recipientKey,
              commitment,
              encryptedNote: encrypted,
              treeIndex:
                Number.isSafeInteger(params.note?.treeIndex) &&
                params.note.treeIndex >= 0
                  ? params.note.treeIndex
                  : undefined,
              txHash: params.txHash,
              createdAt: Date.now(),
              attempts: 0,
              lastError: msg,
            },
            baseCacheSecret,
          );
          if (typeof window !== "undefined") {
            for (const delay of [15_000, 45_000]) {
              const timer = window.setTimeout(() => {
                pendingInboxRetryTimersRef.current.delete(timer);
                void flushPendingInboxNotes().catch((error) => {
                  addLog(
                    `Pending private note retry failed: ${
                      error instanceof Error ? error.message : String(error)
                    }`,
                    "warning",
                  );
                });
              }, delay);
              pendingInboxRetryTimersRef.current.add(timer);
            }
          }
          addLog(
            `${params.label} inbox storage is pending retry: ${msg}`,
            "warning",
            params.txHash,
          );
          return true;
        } catch (retryErr) {
          const retryMsg =
            retryErr instanceof Error ? retryErr.message : String(retryErr);
          addLog(
            `${params.label} inbox store failed and retry could not be queued: ${retryMsg}`,
            "warning",
            params.txHash,
          );
          return false;
        }
      }
    },
    [
      NOTES_API_URL,
      addLog,
      authHeadersForPath,
      flushPendingInboxNotes,
      rememberLocalNote,
      baseCacheSecret,
      wallet.address,
    ],
  );

  const runFlatSettlementJournalFlush = useCallback(async () => {
    if (
      !EVM_CONFIG.JOINSPLIT_V2.ENABLED ||
      !wallet.connected ||
      !wallet.signer ||
      !wallet.spendingKeys
    ) {
      return false;
    }
    const accountAddress = await wallet.signer.getAddress();
    const provider = getBaseReadProvider();
    let allDurable = true;
    // One pool at a time: three concurrent history scans through the shared
    // read queue starve each other into the queue deadline.
    for (const poolAddress of privatePools()) {
      const scope: FlatJoinSplitV2JournalScope = {
        chainId: EVM_CONFIG.CHAIN_ID,
        poolAddress,
        accountAddress,
        spendingKey: wallet.spendingKeys.spendingKey,
      };
      // Recover immutable encrypted prepared snapshots even after all browser
      // storage was lost; do not trust backup metadata as chain finality.
      const { restoreFlatJournalBackups } =
        await import("../../joinsplit-v2/journalBackup");
      try {
        const [
          { restorePrivateSwapAuthorizations },
          { restorePrivateBridgeAuthorizations },
        ] = await Promise.all([
          import("../../joinsplit-v2/privateSwapRecovery"),
          import("../../joinsplit-v2/privateBridgeRecovery"),
        ]);
        const headers = await authHeadersForPath("/api/v1/recovery/journal", {
          interactive: false,
        });
        // Independent sources may fetch/import concurrently; journal mutations
        // already serialize per wallet/pool. Reconcile only once all have settled.
        const restored = await Promise.allSettled([
          restoreFlatJournalBackups(scope, {
            baseUrl: NOTES_API_URL,
            headers,
          }),
          restorePrivateSwapAuthorizations(scope, headers),
          restorePrivateBridgeAuthorizations(scope, headers),
        ]);
        const sources = ["journal backup", "swap recovery", "bridge recovery"];
        for (const [index, result] of restored.entries()) {
          if (
            index === 2 &&
            result.status === "fulfilled" &&
            Number(result.value) > 0
          )
            addLog(
              `Consolidated ${result.value} duplicate bridge recovery entries for pool ${poolAddress.slice(0, 8)}; original recovery records retained.`,
              "info",
            );
          if (result.status === "rejected")
            addLog(
              `${sources[index]} check failed for pool ${poolAddress.slice(0, 8)}: ${recoveryErrorMessage(result.reason)}. Continuing with available local journal records.`,
              "warning",
            );
        }
      } catch (error) {
        // An unavailable backup service cannot erase or suppress recovery from
        // surviving local records. New sends independently require backup ACKs.
        addLog(
          `Recovery restore check failed: ${recoveryErrorMessage(error)}. Continuing with available local journal records.`,
          "warning",
        );
      }
      const reconciliation = await reconcileFlatJoinSplitV2SettlementJournal({
        provider,
        scope,
      });
      for (const [id, issue] of reconciliation.issues) {
        addLog(`Settlement recovery ${id.slice(0, 8)}: ${issue}`, "warning");
      }
      for (const closed of reconciliation.superseded) {
        addLog(
          closed.nullifier
            ? `Settlement recovery ${closed.record.id.slice(0, 8)}: a finalized transaction consumed an input at block ${closed.blockNumber}. The remaining inputs are released and recovery secrets are retained.`
            : `Settlement recovery ${closed.record.id.slice(0, 8)}: closed. Its inputs were spent by ${closed.txHash.slice(0, 10)}… (block ${closed.blockNumber}), a different settlement, so it can never execute and had nothing to recover.`,
          "info",
        );
      }
      for (const record of reconciliation.ambiguous) {
        if (reconciliation.issues.has(record.id)) continue;
        addLog(
          `Settlement recovery ${record.id.slice(0, 8)}: still open, ${reconciliation.reasons.get(record.id) ?? "awaiting chain evidence"}`,
          "info",
        );
      }
      const selfMpkHex = formatMpkHex(wallet.spendingKeys.mpk).toLowerCase();
      const selfInboxPublicKey = formatInboxPublicKeyHex(
        wallet.spendingKeys.spendingKey,
      );
      const consumedByFinalizedSettlement = new Set(
        reconciliation.finalized.flatMap((record) =>
          record.sourceCommitments.map((commitment) =>
            commitment.toLowerCase(),
          ),
        ),
      );
      for (const record of reconciliation.finalized) {
        let currentRecord = record;
        // Outputs the relayer confirms as durably backed up through private-fee
        // recovery may complete without an inbox envelope; nothing else may.
        let feeBackup: {
          quoteId: string;
          txHash: string;
          commitments: Set<string>;
        } | null = null;
        if (poolAddress.toLowerCase() === privateTokenPool()?.toLowerCase()) {
          try {
            const id = settlementQuoteId(record);
            if (!id) {
              addLog(
                `Settlement recovery ${record.id.slice(0, 8)}: fee quote id could not be derived from its transaction`,
                "warning",
              );
            } else if (!record.txHash) {
              addLog(
                `Settlement recovery ${record.id.slice(0, 8)}: no transaction hash recorded yet`,
                "info",
              );
            } else {
              const confirmation = await requestPrivateFees<{
                confirmed: boolean;
                deliveries?: unknown;
              }>(
                "confirm",
                { id, transactionHash: record.txHash },
                { pool: poolAddress },
              );
              if (
                confirmation.confirmed === true &&
                Array.isArray(confirmation.deliveries)
              )
                feeBackup = {
                  quoteId: String(id),
                  txHash: record.txHash,
                  commitments: new Set(
                    confirmation.deliveries
                      .filter(
                        (value): value is string => typeof value === "string",
                      )
                      .map((value) => value.toLowerCase()),
                  ),
                };
              else
                addLog(
                  `Settlement recovery ${record.id.slice(0, 8)}: the relayer has not confirmed the fee backup yet`,
                  "info",
                );
            }
          } catch (error) {
            // Preserve the journal until the durable delivery can be confirmed.
            addLog(
              `Settlement recovery ${record.id.slice(0, 8)}: relayer confirmation unavailable (${error instanceof Error ? error.message : String(error)})`,
              "warning",
            );
          }
        }
        for (const originalDelivery of record.deliveries) {
          let delivery = currentRecord.deliveries.find(
            (candidate) =>
              candidate.commitment.toLowerCase() ===
              originalDelivery.commitment.toLowerCase(),
          );
          if (!delivery) {
            allDurable = false;
            addLog(
              `Settlement recovery ${currentRecord.id.slice(0, 8)}: delivery record for ${originalDelivery.commitment.slice(0, 10)} is missing`,
              "warning",
            );
            continue;
          }
          if (delivery.disposition) continue;
          const note = currentRecord.outputNotes.find(
            (candidate) =>
              candidate.commitment.toLowerCase() ===
              delivery.commitment.toLowerCase(),
          );
          if (!note || note.leafIndex < 0) {
            allDurable = false;
            addLog(
              `Settlement recovery ${currentRecord.id.slice(0, 8)}: output ${delivery.commitment.slice(0, 10)} is not placed in the tree yet`,
              "info",
            );
            continue;
          }
          if (
            consumedByFinalizedSettlement.has(note.commitment.toLowerCase())
          ) {
            currentRecord =
              await markFlatJoinSplitV2SettlementDeliveryCompleted(
                scope,
                currentRecord.id,
                note.commitment,
                "superseded",
              );
            continue;
          }
          if (delivery.keepLocal) {
            rememberLocalNote(note, currentRecord.txHash);
          }
          if (feeBackup?.commitments.has(note.commitment.toLowerCase())) {
            currentRecord =
              await markFlatJoinSplitV2SettlementDeliveryCompleted(
                scope,
                currentRecord.id,
                note.commitment,
                "delivered",
                {
                  durableBackup: {
                    kind: "private-fee-recovery",
                    quoteId: feeBackup.quoteId,
                    txHash: feeBackup.txHash,
                    confirmedAt: Date.now(),
                  },
                },
              );
            continue;
          }
          const isSelf = delivery.recipientMpkHex.toLowerCase() === selfMpkHex;
          const inboxPublicKey =
            delivery.recipientInboxPublicKeyHex ||
            (isSelf ? selfInboxPublicKey : undefined);
          if (!inboxPublicKey) {
            allDurable = false;
            addLog(
              `Settlement recovery ${currentRecord.id.slice(0, 8)}: ${delivery.commitment.slice(0, 10)} has no recipient inbox key; it closes when the relayer confirms the fee backup`,
              "info",
            );
            continue;
          }
          const deliveredNote = note.privateHistory
            ? {
                ...note,
                privateHistory: {
                  ...note.privateHistory,
                  txHash: currentRecord.txHash || note.privateHistory.txHash,
                },
              }
            : note;
          const recipientKey = await hashMpkHex(delivery.recipientMpkHex);
          try {
            if (!delivery.encryptedNote) {
              const encryptedNote = await encryptInboxNoteWithPublicKey(
                deliveredNote,
                inboxPublicKey,
              );
              currentRecord =
                await stageFlatJoinSplitV2SettlementDeliveryEnvelope(
                  scope,
                  currentRecord.id,
                  note.commitment,
                  encryptedNote,
                );
              delivery = currentRecord.deliveries.find(
                (candidate) =>
                  candidate.commitment.toLowerCase() ===
                  note.commitment.toLowerCase(),
              );
            }
            if (!delivery?.encryptedNote) {
              throw new Error("Settlement delivery envelope was not persisted");
            }
            const authHeaders = await authHeadersForPath(
              "/api/v1/notes/store",
              isSelf
                ? {
                    interactive: true,
                    expectedRecipientKey: recipientKey,
                  }
                : { interactive: true },
            );
            await storePrivateInboxNote({
              baseUrl: NOTES_API_URL,
              recipientKey,
              headers: authHeaders,
              encryptedNote: delivery.encryptedNote,
              commitment: note.commitment,
              treeIndex: note.treeIndex,
            });
            currentRecord =
              await markFlatJoinSplitV2SettlementDeliveryCompleted(
                scope,
                currentRecord.id,
                note.commitment,
                "delivered",
              );
          } catch (error) {
            allDurable = false;
            addLog(
              `Settlement note delivery is pending: ${
                error instanceof Error ? error.message : String(error)
              }`,
              "warning",
              currentRecord.txHash,
            );
          }
        }
        const terminal = currentRecord.deliveries.every((delivery) =>
          Boolean(delivery.disposition),
        );
        if (terminal) {
          await removeFlatJoinSplitV2SettlementJournalRecord(
            scope,
            currentRecord.id,
          );
        } else {
          allDurable = false;
          await markFlatJoinSplitV2SettlementDeliveryPending(
            scope,
            currentRecord.id,
          );
        }
      }
      if (reconciliation.ambiguous.length > 0) allDurable = false;
    }
    return allDurable;
  }, [
    NOTES_API_URL,
    addLog,
    authHeadersForPath,
    rememberLocalNote,
    wallet.connected,
    wallet.signer,
    wallet.spendingKeys,
  ]);

  const flushFlatSettlementJournal = useCallback((): Promise<boolean> => {
    if (flatSettlementFlushRef.current) {
      return flatSettlementFlushRef.current;
    }
    const pending = runFlatSettlementJournalFlush();
    flatSettlementFlushRef.current = pending;
    const clear = () => {
      if (flatSettlementFlushRef.current === pending) {
        flatSettlementFlushRef.current = null;
      }
    };
    void pending.then(clear, clear);
    return pending;
  }, [runFlatSettlementJournalFlush]);

  useEffect(() => {
    if (
      !EVM_CONFIG.JOINSPLIT_V2.ENABLED ||
      !wallet.connected ||
      !proxyAuthReady
    )
      return;
    return startRecoveryPolling({
      run: flushFlatSettlementJournal,
      runImmediately: false,
      isPaused: () =>
        Boolean(balancesRefreshRef.current) ||
        hasActiveFlatJoinSplitV2Operation() ||
        document.hidden,
      onError: (error) => {
        addLog(
          `Settlement journal recovery delayed: ${
            error instanceof Error ? error.message : String(error)
          }`,
          "warning",
        );
      },
    });
  }, [addLog, flushFlatSettlementJournal, wallet.connected, proxyAuthReady]);

  const handleBalancesRefresh = useCallback(
    (options?: { background?: boolean }) => {
      if (balancesRefreshRef.current) return balancesRefreshRef.current;
      const isCurrent = () => refreshScopeRef.current === walletSyncScope;
      const pending = (async () => {
        setBalancesRefreshing(true);
        const startedAt = performance.now();
        refreshTimingsRef.current = {};
        try {
          if (!options?.background)
            setStatus({
              type: "info",
              message: "Updating balances…",
            });
          journalCheckRef.current = { settled: false, complete: false };
          const journalCheck = async () => {
            let complete = false;
            try {
              complete = await flushFlatSettlementJournal();
              return complete;
            } finally {
              journalCheckRef.current = { settled: true, complete };
            }
          };
          await refreshPrivateState(journalCheck);
          if (!isCurrent()) return;
          // One line per refresh so a slow refresh names its slow phase.
          const phases = [
            "head",
            "recovery",
            "inbox",
            "validation",
            "local-validation",
          ]
            .filter((stage) => refreshTimingsRef.current[stage])
            .map(
              (stage) =>
                `${stage} ${(refreshTimingsRef.current[stage].durationMs / 1000).toFixed(1)}s${refreshTimingsRef.current[stage].succeeded ? "" : " (failed)"}`,
            );
          addLog(
            `Balance refresh took ${((performance.now() - startedAt) / 1000).toFixed(1)}s${lastRefreshSkippedRef.current ? " (nothing changed)" : ""}: ${phases.join(", ")}`,
            "info",
          );
          if (
            !configuredEvmRecoveryRegistry(EVM_CONFIG.CHAIN_ID) &&
            journalCheckRef.current.settled &&
            !journalCheckRef.current.complete
          ) {
            addLog(
              "Settlement recovery has unresolved records or pending note deliveries; the balance check completed.",
              "info",
            );
          } else if (
            !configuredEvmRecoveryRegistry(EVM_CONFIG.CHAIN_ID) &&
            !journalCheckRef.current.settled
          ) {
            addLog(
              "Settlement journal check continues in the background; its result is logged when it finishes.",
              "info",
            );
          }
          if (!options?.background)
            setStatus({
              type: "success",
              message: lastRefreshSkippedRef.current
                ? "Private balances are up to date."
                : "Private balances refreshed.",
              autoDismissMs: 5_000,
            });
        } catch (error) {
          const detail = error instanceof Error ? error.message : String(error);
          if (!isCurrent()) return;
          addLog(`Private balance recovery failed: ${detail}`, "warning");
          setStatus({
            type: "error",
            message:
              "Could not finish updating balances. Your saved balance is retained. Please try again.",
          });
        } finally {
          if (isCurrent()) setBalancesRefreshing(false);
        }
      })();
      balancesRefreshRef.current = pending;
      const clear = () => {
        if (balancesRefreshRef.current === pending)
          balancesRefreshRef.current = null;
      };
      void pending.then(clear, clear);
      return pending;
    },
    [
      flushFlatSettlementJournal,
      refreshPrivateState,
      setStatus,
      addLog,
      walletSyncScope,
    ],
  );

  useWalletBackgroundRefresh(
    walletSyncScope,
    !!(
      wallet.connected &&
      wallet.spendingKeys &&
      (proxyAuthReady || configuredEvmRecoveryRegistry(EVM_CONFIG.CHAIN_ID))
    ),
    () => handleBalancesRefresh({ background: true }),
  );

  const handleHistoryRefresh = useCallback(async () => {
    setHistoryLoading(true);
    try {
      setHistoryError(null);
      await syncHistoryEntries();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setHistoryError(msg);
    } finally {
      setHistoryLoading(false);
    }
  }, [syncHistoryEntries]);

  const recipientParsed = useMemo(
    () => parsePrivateAddress(sendRecipient),
    [sendRecipient],
  );
  const recipientHexMpk = useMemo(() => {
    const trimmed = sendRecipient.trim();
    if (!trimmed) return null;
    if (ethers.isAddress(trimmed)) return null;
    if (!/^0x[0-9a-fA-F]+$/.test(trimmed)) return null;
    return trimmed;
  }, [sendRecipient]);
  const isPrivateRecipient =
    !!recipientParsed ||
    !!recipientHexMpk ||
    isStealthHandle(sendRecipient.trim());
  const isSendNftToken =
    activeSendToken?.tokenType === "erc721" ||
    activeSendToken?.tokenType === "erc1155" ||
    activeSendToken?.tokenType === "vnft";
  const privateFeePolicy = usePrivateFeePolicy(
    wallet.address,
    params.feeToken,
    {
      action: params.feeDeposit
        ? "deposit"
        : isPrivateRecipient
          ? "p2p"
          : "withdraw",
      amount: sendAmount || "",
      recipient: sendRecipient.trim(),
      notes: params.feeNotes ?? [],
    },
    params.feeQuotePaused === true,
  );
  const actionRates =
    privateFeePolicy?.rates[isPrivateRecipient ? "p2p" : "withdraw"];
  const sendFeesApply =
    (usesPrivateFees() || !isPrivateRecipient) && !isSendNftToken;
  const protocolFeeBps = usesPrivateFees()
    ? (actionRates?.protocolBps ?? 0)
    : EVM_CONFIG.JOINSPLIT_V2.ENABLED
      ? EVM_CONFIG.JOINSPLIT_V2.RELAY_FEE_BPS
      : EVM_CONFIG.PROTOCOL_FEE_BPS || 0;
  const partnerFeeBps = usesPrivateFees()
    ? (actionRates?.partnerBps ?? 0)
    : EVM_CONFIG.JOINSPLIT_V2.ENABLED
      ? 0
      : EVM_CONFIG.PARTNER_FEE_BPS || 0;
  const canUseBatchWithdraw =
    !EVM_CONFIG.JOINSPLIT_V2.ENABLED &&
    (!activeSendToken?.tokenType || activeSendToken.tokenType === "erc20");
  const parsedSendAmount = Number(sendAmount || "0");
  let sendProtocolFee = Number.isFinite(parsedSendAmount)
    ? (parsedSendAmount * protocolFeeBps) / 10_000
    : 0;
  if (EVM_CONFIG.JOINSPLIT_V2.ENABLED && !usesPrivateFees()) {
    sendProtocolFee = 0;
    if (sendFeesApply && EVM_CONFIG.JOINSPLIT_V2.RELAY_WITHDRAWALS) {
      try {
        const decimals = activeSendToken?.decimals ?? 18;
        // The live quote's fee on quoted networks; zero while it loads (the
        // form shows the pending message and cannot continue).
        const relayFee: ((amount: bigint) => bigint) | null =
          params.relayFee === undefined ? flatRelayFee : params.relayFee;
        sendProtocolFee = relayFee
          ? Number(
              ethers.formatUnits(
                relayFee(ethers.parseUnits(sendAmount || "0", decimals)),
                decimals,
              ),
            )
          : 0;
      } catch {
        /* The amount input reports invalid values. */
      }
    }
  }
  const sendPartnerFee = Number.isFinite(parsedSendAmount)
    ? (parsedSendAmount * partnerFeeBps) / 10_000
    : 0;
  const batchSendTotalAmount = useMemo(
    () =>
      sendBatchRows.reduce(
        (acc, row) => acc + (Number(row.amount || "0") || 0),
        0,
      ),
    [sendBatchRows],
  );
  let batchSendProtocolFee = Number.isFinite(batchSendTotalAmount)
    ? (batchSendTotalAmount * protocolFeeBps) / 10_000
    : 0;
  if (EVM_CONFIG.JOINSPLIT_V2.ENABLED && !usesPrivateFees()) {
    batchSendProtocolFee = 0;
    if (EVM_CONFIG.JOINSPLIT_V2.RELAY_WITHDRAWALS) {
      try {
        const decimals = activeSendToken?.decimals ?? 18;
        const total = sendBatchRows.reduce(
          (sum, row) => sum + ethers.parseUnits(row.amount || "0", decimals),
          0n,
        );
        const relayFee: ((amount: bigint) => bigint) | null =
          params.relayFee === undefined ? flatRelayFee : params.relayFee;
        batchSendProtocolFee = relayFee
          ? Number(ethers.formatUnits(relayFee(total), decimals))
          : 0;
      } catch {
        /* Each row reports invalid values. */
      }
    }
  }
  const batchSendPartnerFee = Number.isFinite(batchSendTotalAmount)
    ? (batchSendTotalAmount * partnerFeeBps) / 10_000
    : 0;

  const clearSendForm = useCallback(() => {
    setSendRecipient("");
    setSendAmount("");
    setSendMemo("");
    setSendBatchMode(false);
    setSendBatchRows([{ recipient: "", amount: "" }]);
  }, []);

  const handleBatchFileUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;
      try {
        const parsed = await parseBatchRecipientFile(file);
        setSendBatchMode(true);
        setSendBatchRows(parsed);
        setStatus({
          type: "success",
          message: `Loaded ${parsed.length} batch recipients from file.`,
        });
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setStatus({ type: "error", message: `Failed to parse file: ${msg}` });
      } finally {
        if (event.target) event.target.value = "";
      }
    },
    [setStatus],
  );

  const enterBatchMode = useCallback(() => {
    if (!canUseBatchWithdraw) return;
    const firstRecipient = sendRecipient.trim();
    const firstAmount = sendAmount.trim();
    setSendBatchRows((prev) => {
      if (prev.length > 1) return prev;
      return [
        { recipient: firstRecipient, amount: firstAmount },
        { recipient: "", amount: "" },
      ];
    });
    setSendBatchMode(true);
  }, [canUseBatchWithdraw, sendRecipient, sendAmount]);

  const clearSwapForm = useCallback(() => {
    setSwapAmount("");
    setSwapQuote(null);
    setSwapQuoteError(null);
  }, []);

  const requireMoneyOperationAuth = async (): Promise<boolean> => {
    try {
      await ensureProxyAuthToken({ interactive: true });
      return true;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setStatus({
        type: "error",
        message: `Sign in required before deposits or transactions: ${msg}`,
      });
      return false;
    }
  };

  useEffect(() => {
    const wasConnected = prevWalletConnectedRef.current;
    prevWalletConnectedRef.current = wallet.connected;
    if (wasConnected && !wallet.connected && typeof window !== "undefined") {
      clearProxyAuthToken();
      window.location.reload();
      return;
    }
    if (wallet.connected) return;
    setStatus(null);
    if (isPaymentMode) return;
    clearSendForm();
    clearSwapForm();
    setDepositRecipient("");
    setPaymentLinkAmountInput("");
    setPaymentLinkToken(null);
    setDepositAmount("0.01");
  }, [wallet.connected, clearSendForm, clearSwapForm, isPaymentMode]);

  useEffect(() => {
    if (!canUseBatchWithdraw && sendBatchMode) {
      setSendBatchMode(false);
    }
  }, [canUseBatchWithdraw, sendBatchMode]);

  const ensureWalletCompliant = useCallback(() => {
    if (!enableRangeScreening) return true;
    if (screeningStatus === "checking" || screeningStatus === "idle") {
      setStatus({
        type: "error",
        message: "Compliance screening is still running. Please wait.",
      });
      return false;
    }
    if (screeningStatus === "blocked") {
      setStatus({
        type: "error",
        message: screeningError
          ? `Wallet blocked: ${screeningError}`
          : "Wallet blocked by compliance screening.",
      });
      return false;
    }
    if (screeningStatus === "error") {
      setStatus({
        type: "error",
        message: "Compliance check failed. Please reconnect and try again.",
      });
      return false;
    }
    return true;
  }, [enableRangeScreening, screeningStatus, screeningError, setStatus]);
  const ensureShieldDepositReady = useCallback(
    async (recipientMpk?: bigint, recipientInboxPublicKeyHex?: string) => {
      if (!wallet.spendingKeys) {
        throw new Error(
          "Wallet spending keys unavailable for deposit readiness check",
        );
      }

      if (EVM_CONFIG.JOINSPLIT_V2.ENABLED) {
        const isSelfRecipient =
          recipientMpk === undefined ||
          recipientMpk === wallet.spendingKeys.mpk;
        if (!isSelfRecipient && !recipientInboxPublicKeyHex) {
          throw new Error(
            "Private recipient must include an inbox public key (use MPK:IPK or a .stealth handle)",
          );
        }
        // Fresh-pool deposits are indexed and authenticated from chain events;
        // the legacy depth-16 engine is not a settlement-readiness dependency.
        return;
      }

      const healthResp = await fetch(
        buildEngineUrl(NOTES_API_URL, "/api/v1/public/merkle/latest"),
      );
      if (!healthResp.ok) {
        throw new Error(`Base engine unavailable (${healthResp.status})`);
      }

      const selfMpkHex = formatMpkHex(wallet.spendingKeys.mpk);
      const selfRecipientKey = await hashMpkHex(selfMpkHex);
      const targetMpkHex = recipientMpk
        ? formatMpkHex(recipientMpk)
        : selfMpkHex;
      const targetRecipientKey = await hashMpkHex(targetMpkHex);
      const targetRecoveryKey =
        await deriveRecoveryKeyFromRecipientKey(targetRecipientKey);
      const isSelfRecipient = targetRecipientKey === selfRecipientKey;

      if (!isSelfRecipient && !recipientInboxPublicKeyHex) {
        throw new Error(
          "Private recipient must include an inbox public key (use MPK:IPK or a .stealth handle)",
        );
      }

      if (!isSelfRecipient) {
        return;
      }

      const registerHeaders = await authHeadersForPath(
        "/api/v1/recovery/register",
        {
          interactive: true,
          expectedRecipientKey: targetRecipientKey,
        },
      );
      const registerResp = await fetch(
        buildEngineUrl(NOTES_API_URL, "/api/v1/recovery/register"),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...registerHeaders,
          },
          body: JSON.stringify({
            recipient_key: targetRecipientKey,
            recovery_key: targetRecoveryKey,
          }),
        },
      );
      if (!registerResp.ok) {
        const errorText = await registerResp.text().catch(() => "");
        throw new Error(
          `Recovery registration failed${errorText ? `: ${errorText}` : ""}`,
        );
      }

      const readinessHeaders = await authHeadersForPath(
        "/api/v1/recovery/ready",
        {
          interactive: true,
          expectedRecipientKey: targetRecipientKey,
        },
      );
      const readinessResp = await fetch(
        buildEngineUrl(NOTES_API_URL, "/api/v1/recovery/ready"),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...readinessHeaders,
          },
          body: JSON.stringify({
            recipient_key: targetRecipientKey,
            recovery_key: targetRecoveryKey,
          }),
        },
      );
      if (!readinessResp.ok) {
        const errorText = await readinessResp.text().catch(() => "");
        throw new Error(
          `Recovery readiness check failed${errorText ? `: ${errorText}` : ""}`,
        );
      }

      const readiness = (await readinessResp.json()) as {
        ready?: boolean;
        pendingCount?: number;
        pending_count?: number;
      };
      if (!readiness.ready) {
        throw new Error(
          "Recovery backend is not ready for this wallet yet. Please retry in a moment.",
        );
      }
    },
    [NOTES_API_URL, authHeadersForPath, wallet.spendingKeys],
  );
  const fundPerpsVaultFromPrivateAsset = useCallback(
    async (params: {
      vaultAddress: string;
      amountRaw: bigint;
      token: TokenInfo;
    }): Promise<boolean> => {
      const assetToken = params.token;
      if (!assetToken || assetToken.isNative) {
        setStatus({
          type: "error",
          message: "Perps asset token config missing for vault auto-funding",
        });
        return false;
      }
      if (!ethers.isAddress(params.vaultAddress)) {
        setStatus({
          type: "error",
          message: "Invalid vault address for perps auto-funding",
        });
        return false;
      }
      if (params.amountRaw <= 0n) {
        return true;
      }
      const amountInput = ethers.formatUnits(
        params.amountRaw,
        assetToken.decimals,
      );
      setLoading(true);
      setStatus({
        type: "info",
        message: `Funding perps vault with ${amountInput} ${assetToken.symbol} from private balance...`,
      });
      try {
        const result = await handleSend(
          params.vaultAddress,
          amountInput,
          assetToken,
        );
        if (
          currentWalletRef.current?.address !== wallet?.address ||
          currentWalletRef.current?.spendingKeys?.mpk !==
            wallet?.spendingKeys?.mpk
        )
          return false;
        if (result.migrationNotes?.length) {
          setNotes((previous) =>
            mergeMigrationNotes(
              previous,
              result.migrationNotes!,
              result.spentCommitments,
            ),
          );
        }
        if (result.mode !== "withdraw" || !result.txHash) {
          setStatus({
            type: "error",
            message: "Perps vault auto-funding failed",
          });
          return false;
        }
        if ((result.spentCommitments?.length ?? 0) > 0) {
          const spentSet = new Set(
            result.spentCommitments!.map((c) => c.toLowerCase()),
          );
          setPendingSpentCommitments((prev) => {
            const next = new Set(prev);
            spentSet.forEach((c) => next.add(c));
            return next;
          });
          setNotes((prev) => {
            const next = prev.map((n) =>
              spentSet.has(n.note.commitment.toLowerCase())
                ? { ...n, spent: true }
                : n,
            );
            if (
              result.changeNote &&
              !next.some(
                (n) =>
                  n.note.commitment.toLowerCase() ===
                  result.changeNote!.commitment.toLowerCase(),
              )
            ) {
              next.push({
                note: result.changeNote,
                timestamp: Date.now(),
                txHash: result.txHash,
              });
            }
            return next;
          });
        }
        pushHistoryEntry({
          type: "withdraw",
          amount: amountInput,
          symbol: assetToken.symbol,
          txHash: result.txHash,
        });
        await storeHistoryEvent({
          type: "withdraw",
          amount: amountInput,
          symbol: assetToken.symbol,
          txHash: result.txHash,
        });
        if (result.changeNote && wallet.spendingKeys) {
          const recipientMpkHex = formatMpkHex(wallet.spendingKeys.mpk);
          const recipientKey = await hashMpkHex(recipientMpkHex);
          const recipientInboxPublicKey = formatInboxPublicKeyHex(
            wallet.spendingKeys.spendingKey,
          );

          try {
            const commitAuthHeaders = await authHeadersForPath(
              "/api/v1/commitments",
            );
            await storePrivateCommitment({
              baseUrl: NOTES_API_URL,
              addressKey: recipientKey,
              headers: commitAuthHeaders,
              commitment: {
                commitment: result.changeNote.commitment,
                amount: ethers.formatUnits(
                  result.changeNote.value,
                  assetToken.decimals,
                ),
                amountWei: result.changeNote.value.toString(),
                leafIndex: result.changeNote.leafIndex,
                spent: false,
                encryptedKeys: {},
                txHash: result.txHash,
                timestamp: Math.floor(Date.now() / 1000),
              },
              encryptTarget: {
                inboxPublicKeyHex: recipientInboxPublicKey,
              },
            });
          } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            addLog(
              `Perps auto-fund change commitment store warning: ${msg}`,
              "warning",
            );
          }

          await persistInboxNoteOrQueue({
            label: "Perps auto-fund change",
            note: result.changeNote,
            recipientKey,
            inboxPublicKeyHex: recipientInboxPublicKey,
            txHash: result.txHash,
            keepLocal: true,
          });
        }
        await refreshPrivateState();
        addLog(
          `Perps vault funded with ${amountInput} ${assetToken.symbol}`,
          "success",
          result.txHash,
        );
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        setStatus({
          type: "error",
          message: `Perps vault auto-funding failed: ${message}`,
        });
        return false;
      } finally {
        setLoading(false);
      }
    },
    [
      addLog,
      authHeadersForPath,
      handleSend,
      persistInboxNoteOrQueue,
      pushHistoryEntry,
      refreshPrivateState,
      setLoading,
      setNotes,
      setStatus,
      storeHistoryEvent,
      wallet.spendingKeys,
    ],
  );
  const shieldPerpsWithdrawAssetToPrivate = useCallback(
    async (params: {
      amountRaw: bigint;
      token: TokenInfo;
    }): Promise<boolean> => {
      const assetToken = params.token;
      if (!assetToken || assetToken.isNative) {
        setStatus({
          type: "error",
          message: "Perps asset token config missing for auto-shield",
        });
        return false;
      }
      if (params.amountRaw <= 0n) return true;
      const amountInput = ethers.formatUnits(
        params.amountRaw,
        assetToken.decimals,
      );
      await ensureShieldDepositReady();
      const approved = await approveTokenSpend(assetToken, params.amountRaw);
      if (!approved) {
        setStatus({
          type: "error",
          message: `${assetToken.symbol} approval failed for perps auto-shield`,
        });
        return false;
      }
      setLoading(true);
      setStatus({
        type: "info",
        message: `Shielding ${amountInput} ${assetToken.symbol} back to private balance...`,
      });
      try {
        const result = await handleShield(
          amountInput,
          undefined,
          undefined,
          assetToken,
        );
        if (!result?.success || !result.note) {
          setStatus({ type: "error", message: "Perps auto-shield failed" });
          return false;
        }
        setNotes((prev) => {
          const existing = prev.some(
            (n) =>
              n.note.commitment.toLowerCase() ===
              result.note.commitment.toLowerCase(),
          );
          if (existing) return prev;
          return [
            ...prev,
            { note: result.note, timestamp: Date.now(), txHash: result.txHash },
          ];
        });
        setStatus({
          type: "success",
          message: `Added ${amountInput} ${assetToken.symbol}`,
        });
        pushHistoryEntry({
          type: "deposit",
          amount: amountInput,
          symbol: assetToken.symbol,
          txHash: result.txHash,
        });
        await storeHistoryEvent({
          type: "deposit",
          amount: amountInput,
          symbol: assetToken.symbol,
          txHash: result.txHash,
        });
        await refreshPrivateState();
        addLog(
          `Perps withdraw auto-shielded ${amountInput} ${assetToken.symbol}`,
          "success",
          result.txHash,
        );
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        setStatus({
          type: "error",
          message: `Perps auto-shield failed: ${message}`,
        });
        return false;
      } finally {
        setLoading(false);
      }
    },
    [
      addLog,
      approveTokenSpend,
      ensureShieldDepositReady,
      handleShield,
      pushHistoryEntry,
      refreshPrivateState,
      setLoading,
      setNotes,
      setStatus,
      storeHistoryEvent,
    ],
  );
  const ingestPerpsWithdrawPrivateNote = useCallback(
    async (params: {
      note: any;
      txHash?: string;
      amountRaw: bigint;
      token: TokenInfo;
    }): Promise<boolean> => {
      const assetToken = params.token;
      if (!assetToken || assetToken.isNative) {
        setStatus({
          type: "error",
          message:
            "Perps asset token config missing for private note ingestion",
        });
        return false;
      }
      if (!wallet.spendingKeys) {
        setStatus({
          type: "error",
          message:
            "Wallet spending keys unavailable for perps private note ingestion",
        });
        return false;
      }
      const amountInput = ethers.formatUnits(
        params.amountRaw,
        assetToken.decimals,
      );
      const note = params.note;
      if (!params.txHash || !ethers.isHexString(params.txHash, 32)) {
        setStatus({
          type: "error",
          message:
            "Perps private note ingestion requires a confirmed deposit transaction hash",
        });
        return false;
      }
      if (!Number.isInteger(note.leafIndex) || note.leafIndex < 0) {
        setStatus({
          type: "error",
          message:
            "Perps private note ingestion requires a confirmed Merkle leaf index",
        });
        return false;
      }
      try {
        setNotes((prev) => {
          const existing = prev.some(
            (n) =>
              n.note.commitment.toLowerCase() ===
              String(note.commitment).toLowerCase(),
          );
          if (existing) return prev;
          return [
            ...prev,
            { note, timestamp: Date.now(), txHash: params.txHash || "" },
          ];
        });

        const recipientMpkHex = formatMpkHex(wallet.spendingKeys.mpk);
        const recipientKey = await hashMpkHex(recipientMpkHex);
        const recipientInboxPublicKey = formatInboxPublicKeyHex(
          wallet.spendingKeys.spendingKey,
        );

        try {
          const commitAuthHeaders = await authHeadersForPath(
            "/api/v1/commitments",
            {
              expectedRecipientKey: recipientKey,
            },
          );
          await storePrivateCommitment({
            baseUrl: NOTES_API_URL,
            addressKey: recipientKey,
            headers: commitAuthHeaders,
            commitment: {
              commitment: String(note.commitment),
              amount: amountInput,
              amountWei: params.amountRaw.toString(),
              leafIndex:
                typeof note.leafIndex === "number" ? note.leafIndex : 0,
              spent: false,
              encryptedKeys: {},
              txHash: params.txHash || "",
              timestamp: Math.floor(Date.now() / 1000),
            },
            encryptTarget: {
              inboxPublicKeyHex: recipientInboxPublicKey,
            },
          });
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          addLog(`Perps withdraw commitment store warning: ${msg}`, "warning");
        }

        await persistInboxNoteOrQueue({
          label: "Perps withdraw",
          note,
          recipientKey,
          inboxPublicKeyHex: recipientInboxPublicKey,
          txHash: params.txHash || "",
          keepLocal: true,
        });

        pushHistoryEntry({
          type: "deposit",
          amount: amountInput,
          symbol: assetToken.symbol,
          txHash: params.txHash || "",
        });
        await storeHistoryEvent({
          type: "deposit",
          amount: amountInput,
          symbol: assetToken.symbol,
          txHash: params.txHash || "",
        });
        await refreshPrivateState();
        addLog(
          `Perps withdraw re-shielded ${amountInput} ${assetToken.symbol} into private balance`,
          "success",
          params.txHash,
        );
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        setStatus({
          type: "error",
          message: `Perps private note ingestion failed: ${message}`,
        });
        return false;
      }
    },
    [
      addLog,
      authHeadersForPath,
      persistInboxNoteOrQueue,
      pushHistoryEntry,
      refreshPrivateState,
      setNotes,
      setStatus,
      storeHistoryEvent,
      wallet.spendingKeys,
    ],
  );
  const complianceToast = useComplianceToast({
    enabled: enableRangeScreening,
    connected: wallet.connected,
    screeningStatus,
    screeningError,
  });
  const perps = usePerpsTabController({
    wallet: {
      address: wallet.address,
      signer: wallet.signer,
      spendingKeys: wallet.spendingKeys,
    },
    chainId: Number(chainId || EVM_CONFIG.CHAIN_ID),
    setStatus,
    setLoading,
    addLog,
    configuredPerpsAssetToken,
    ensureWalletCompliant,
    authHeadersForPath,
    fundVaultFromPrivateAsset: fundPerpsVaultFromPrivateAsset,
    shieldWalletAssetToPrivate: shieldPerpsWithdrawAssetToPrivate,
    ingestPerpsWithdrawPrivateNote,
  });

  const handleShieldClick = async () => {
    if (isPaymentMode) {
      const error = paymentLinkNetworkError(
        window.location.search,
        EVM_CONFIG.CHAIN_ID,
      );
      if (error) {
        setStatus({ type: "error", message: error });
        return;
      }
    }
    if (PREVIOUS_POOL_MODE) {
      setStatus({
        type: "error",
        message: "Open your current wallet to add funds.",
      });
      return;
    }
    const invalid =
      activeDepositToken &&
      amountValidation({
        amount: depositAmount,
        decimals: activeDepositToken.decimals,
        symbol: activeDepositToken.symbol,
        balance: walletTokenBalance,
        native: activeDepositToken.isNative,
        policy: privateFeePolicy,
        action: "deposit",
      }).error;
    if (
      !activeDepositToken ||
      (isPaymentMode && !isPaymentToken(activeDepositToken)) ||
      invalid
    ) {
      setStatus({
        type: "error",
        message: invalid || "This asset is not supported for payments.",
      });
      return;
    }
    setDepositProgress?.("Preparing deposit…");
    if (!ensureWalletCompliant()) return;
    if (!(await requireMoneyOperationAuth())) return;
    const parsedPrivate = parsePrivateAddress(depositRecipient);
    const recipientMpk = parsedPrivate?.mpkHex
      ? BigInt(parsedPrivate.mpkHex)
      : parseMpk(depositRecipient);
    if (depositRecipient && !recipientMpk) {
      setStatus({
        type: "error",
        message: "Recipient MPK must be 0x-prefixed hex",
      });
      return;
    }

    const token = activeDepositToken;
    if (!token) {
      setStatus({
        type: "error",
        message: "Enter a valid NFT contract and token ID",
      });
      return;
    }
    if (
      (token.tokenType === "erc721" || token.tokenType === "erc1155") &&
      !token.tokenSubID
    ) {
      setStatus({ type: "error", message: "NFT token ID is required" });
      return;
    }
    const amountInput = token.tokenType === "erc721" ? "1" : depositAmount;
    let attemptedDeposit = false;
    let privateSyncSucceeded = false;
    let privateSyncPending = false;
    let settledDepositPersistence: (() => Promise<void>) | undefined;
    let privateSyncTarget:
      | {
          recipientKey: string;
          recipientInboxPublicKey: string;
          selfRecipientKey: string;
        }
      | undefined;
    flushSync(() => {
      setLoading(true);
      setStatus({
        type: "info",
        message: token.isNative
          ? `Preparing ${token.symbol} deposit...`
          : `Preparing ${token.symbol} deposit and wallet approval...`,
      });
    });
    await waitForNextPaint();
    try {
      const currentBalance = await readWalletTokenBalance(wallet.signer, token);
      const current = amountValidation({
        amount: amountInput,
        decimals: token.decimals,
        symbol: token.symbol,
        balance: currentBalance,
        native: token.isNative,
        policy: privateFeePolicy,
        action: "deposit",
      });
      if (current.error) {
        void fetchTokenBalance(token);
        throw Error(current.error);
      }
      if (
        !token.isNative &&
        (await wallet.signer.provider.getBalance(wallet.address)) === 0n
      )
        throw Error(
          `Insufficient ${EVM_CONFIG.NETWORK.nativeSymbol} to pay the network fee. Add ${EVM_CONFIG.NETWORK.nativeSymbol} to your connected wallet.`,
        );
      setDepositProgress?.("Preparing transaction…");
      await ensureShieldDepositReady(
        recipientMpk || undefined,
        parsedPrivate?.inboxPublicKeyHex,
      );

      if (!wallet.spendingKeys) {
        throw new Error(
          "Wallet spending keys unavailable for private note storage",
        );
      }
      const selfMpkHex = formatMpkHex(wallet.spendingKeys.mpk);
      const recipientMpkHex = recipientMpk
        ? formatMpkHex(recipientMpk)
        : selfMpkHex;
      const recipientKey = await hashMpkHex(recipientMpkHex);
      const selfRecipientKey = await hashMpkHex(selfMpkHex);
      const recipientInboxPublicKey =
        parsedPrivate?.inboxPublicKeyHex ||
        formatInboxPublicKeyHex(wallet.spendingKeys.spendingKey);
      const isSelfRecipient = recipientKey === selfRecipientKey;
      if (isSelfRecipient) {
        const accessHeaders = await authHeadersForPath("/api/v1/notes", {
          expectedRecipientKey: recipientKey,
        });
        await assertPrivateStorageAccess({
          baseUrl: NOTES_API_URL,
          recipientKey,
          headers: accessHeaders,
        });
      }
      privateSyncTarget = {
        recipientKey,
        recipientInboxPublicKey,
        selfRecipientKey,
      };

      if (!token.isNative) {
        const depositWei = ethers.parseUnits(
          amountInput || "0",
          token.decimals,
        );
        if (depositWei <= 0n) {
          setStatus({ type: "error", message: "Enter a valid amount" });
          return;
        }
        setDepositProgress?.("Approving token in your wallet…");
        const approved = await approveTokenSpend(token, depositWei);
        if (!approved) {
          setStatus({
            type: "error",
            message: `${token.symbol} approval failed`,
          });
          return;
        }
      }

      attemptedDeposit = true;
      const result = await handleShield(
        amountInput,
        recipientMpk || undefined,
        parsedPrivate?.inboxPublicKeyHex,
        token,
        depositMaxQuote ?? undefined,
      );
      if (result?.success && !result.note) {
        throw new Error(
          "Deposit confirmed, but the private note was not returned",
        );
      }
      if (result?.success) {
        // handleShield returns only after chain-bound deposit evidence and the
        // configured confirmation depth have passed. Its encrypted recovery
        // snapshot was acknowledged before submission; inbox/history delivery
        // must not delay displaying this verified self-deposit.
        if (EVM_CONFIG.JOINSPLIT_V2.ENABLED && isSelfRecipient) {
          rememberLocalNote(result.note, result.txHash);
        }
        setStatus({
          type: "success",
          message: `Added ${amountInput} ${token.symbol}`,
        });
        const historySymbol = token.isWrappedNft ? "vSTEALTH" : token.symbol;
        if (isPaymentMode && result.txHash) setLastPaymentTxHash(result.txHash);
        pushHistoryEntry({
          type: "deposit",
          amount: amountInput,
          symbol: historySymbol,
          txHash: result?.txHash,
        });
        const historyEvent = {
          type: "deposit",
          amount: amountInput,
          symbol: historySymbol,
          txHash: result?.txHash,
        };
        if (EVM_CONFIG.JOINSPLIT_V2.ENABLED) {
          privateSyncPending = true;
          settledDepositPersistence = async () => {
            const results = await Promise.allSettled([
              Promise.resolve().then(() => flushFlatSettlementJournal()),
              Promise.resolve().then(() => storeHistoryEvent(historyEvent)),
            ]);
            for (const outcome of results) {
              if (outcome.status === "rejected") {
                addLog(
                  `Deposit confirmed; recovery or history sync delayed: ${
                    outcome.reason instanceof Error
                      ? outcome.reason.message
                      : String(outcome.reason)
                  }`,
                  "warning",
                  result.txHash,
                );
              }
            }
          };
        } else {
          await storeHistoryEvent(historyEvent);
        }
        if (!isPaymentMode) {
          setDepositAmount("");
        }
      }

      if (result?.success && result.note) {
        if (!privateSyncTarget) {
          throw new Error(
            "Deposit confirmed, but private note storage target was not initialized",
          );
        }

        if (!EVM_CONFIG.JOINSPLIT_V2.ENABLED) {
          let encryptedForRetry:
            | {
                ephemeralPubKey: string;
                ciphertext: string;
                iv: string;
                authTag: string;
              }
            | undefined;
          let noteForInbox = result.note;
          try {
            const syncIsSelfRecipient =
              privateSyncTarget.recipientKey ===
              privateSyncTarget.selfRecipientKey;
            if (syncIsSelfRecipient) {
              const commitAuthHeaders = await authHeadersForPath(
                "/api/v1/commitments",
                {
                  expectedRecipientKey: privateSyncTarget.recipientKey,
                },
              );
              await storePrivateCommitment({
                baseUrl: NOTES_API_URL,
                addressKey: privateSyncTarget.recipientKey,
                headers: commitAuthHeaders,
                commitment: {
                  commitment: result.note.commitment,
                  amount: amountInput,
                  amountWei: result.note.value.toString(),
                  leafIndex: result.note.leafIndex,
                  spent: false,
                  encryptedKeys: {},
                  txHash: result.txHash,
                  timestamp: Math.floor(Date.now() / 1000),
                },
                encryptTarget: {
                  inboxPublicKeyHex: privateSyncTarget.recipientInboxPublicKey,
                },
              });
            }

            noteForInbox = syncIsSelfRecipient
              ? result.note
              : {
                  ...result.note,
                  privateHistory: {
                    type: "receive" as const,
                    amount: amountInput,
                    symbol: token.isWrappedNft ? "vSTEALTH" : token.symbol,
                    txHash: result.txHash,
                    timestamp: Date.now(),
                  },
                };
            const encrypted = await encryptInboxNoteWithPublicKey(
              noteForInbox,
              privateSyncTarget.recipientInboxPublicKey,
            );
            encryptedForRetry = encrypted;
            const authHeaders = await authHeadersForPath(
              "/api/v1/notes/store",
              syncIsSelfRecipient
                ? {
                    interactive: true,
                    expectedRecipientKey: privateSyncTarget.recipientKey,
                  }
                : { interactive: true },
            );
            await storePrivateInboxNote({
              baseUrl: NOTES_API_URL,
              recipientKey: privateSyncTarget.recipientKey,
              headers: authHeaders,
              encryptedNote: encrypted,
              commitment: result.note.commitment,
              treeIndex: result.note.treeIndex,
            });
            privateSyncSucceeded = true;
          } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            privateSyncPending = true;
            if (
              privateSyncTarget.recipientKey ===
              privateSyncTarget.selfRecipientKey
            ) {
              setNotes((prev) => {
                if (
                  prev.some(
                    (entry) =>
                      entry.note.commitment.toLowerCase() ===
                      result.note!.commitment.toLowerCase(),
                  )
                ) {
                  return prev;
                }
                return [
                  ...prev,
                  {
                    note: result.note!,
                    timestamp: Date.now(),
                    txHash: result.txHash,
                  },
                ];
              });
            }
            try {
              if (!encryptedForRetry) {
                encryptedForRetry = await encryptInboxNoteWithPublicKey(
                  noteForInbox ?? result.note,
                  privateSyncTarget.recipientInboxPublicKey,
                );
              }
              if (!baseCacheSecret) {
                throw new Error("Encrypted retry cache is unavailable");
              }
              await upsertEncryptedPendingInboxNote(
                wallet.address,
                {
                  recipientKey: privateSyncTarget.recipientKey,
                  commitment: result.note.commitment,
                  encryptedNote: encryptedForRetry,
                  txHash: result.txHash,
                  createdAt: Date.now(),
                  attempts: 0,
                  lastError: msg,
                },
                baseCacheSecret,
              );
              if (typeof window !== "undefined") {
                for (const delay of [15_000, 45_000]) {
                  const timer = window.setTimeout(() => {
                    pendingInboxRetryTimersRef.current.delete(timer);
                    void flushPendingInboxNotes().catch((error) => {
                      addLog(
                        `Pending private note retry failed: ${
                          error instanceof Error ? error.message : String(error)
                        }`,
                        "warning",
                      );
                    });
                  }, delay);
                  pendingInboxRetryTimersRef.current.add(timer);
                }
              }
            } catch (retryErr) {
              addLog(
                `Deposit confirmed; private note retry could not be queued: ${
                  retryErr instanceof Error
                    ? retryErr.message
                    : String(retryErr)
                }`,
                "warning",
                result.txHash,
              );
            }
            addLog(
              `Deposit confirmed; private balance sync is finishing in the background: ${msg}`,
              "info",
              result.txHash,
            );
            setStatus({
              type: "info",
              message:
                "Deposit confirmed. Private balance sync is finishing in the background.",
              autoDismissMs: 8_000,
            });
          }
        }

        // Cross-recipient history is recipient-scoped and requires the recipient's own session.
        // The sender only delivers the encrypted inbox note.
      }
    } catch (error) {
      const message = friendlyError(error);
      setStatus({ type: "error", message });
    } finally {
      if (settledDepositPersistence) {
        setLoading(false);
        await settledDepositPersistence();
      }
      if (attemptedDeposit && (privateSyncSucceeded || privateSyncPending)) {
        try {
          await refreshPrivateState();
        } catch (refreshErr) {
          addLog(
            `Deposit confirmed; private state refresh warning: ${
              refreshErr instanceof Error
                ? refreshErr.message
                : String(refreshErr)
            }`,
            "warning",
          );
        }
      }
      if (!settledDepositPersistence) setLoading(false);
    }
  };

  const handleSendClick = async () => {
    if (!sendBatchMode && activeSendToken) {
      const validation = amountValidation({
        amount: sendAmount,
        decimals: activeSendToken.decimals,
        symbol: activeSendToken.symbol,
        balance: spendablePrivateBalance,
        privateBalance: true,
        policy: privateFeePolicy,
        action: ethers.isAddress(sendRecipient.trim()) ? "withdraw" : "p2p",
      });
      if (validation.error) {
        setStatus({ type: "error", message: validation.error });
        return;
      }
    }
    if (
      PREVIOUS_POOL_MODE &&
      (sendBatchMode || !ethers.isAddress(sendRecipient.trim()))
    ) {
      setStatus({
        type: "error",
        message: "Withdraw your previous balance to a public wallet address.",
      });
      return;
    }
    if (!ensureWalletCompliant()) return;
    if (!(await requireMoneyOperationAuth())) return;
    setLoading(true);
    const token = activeSendToken;
    if (!token) {
      setStatus({
        type: "error",
        message: "Enter a valid NFT contract and token ID",
      });
      setLoading(false);
      return;
    }
    if (
      (token.tokenType === "erc721" ||
        token.tokenType === "erc1155" ||
        token.tokenType === "vnft") &&
      !token.tokenSubID
    ) {
      setStatus({ type: "error", message: "NFT token ID is required" });
      setLoading(false);
      return;
    }
    const isSingleUnitSendToken =
      token.tokenType === "erc721" ||
      (token.tokenType === "vnft" &&
        token.wrappedUnderlyingTokenType !== "erc1155");
    const amountInput = isSingleUnitSendToken ? sendAmount || "1" : sendAmount;
    const transferMemo = sendMemo.trim();
    let settledSendPersistence: (() => Promise<void>) | undefined;
    try {
      if (sendBatchMode) {
        const batchRows = sendBatchRows
          .map((row) => ({
            recipient: row.recipient.trim(),
            amountStr: row.amount.trim(),
          }))
          .filter((row) => row.recipient && row.amountStr);
        const result = await handleBatchWithdraw(batchRows, token);
        const historySymbol = token.isWrappedNft ? "vSTEALTH" : token.symbol;
        if (result.mode === "transfer_batch") {
          pushHistoryEntry({
            type: "transfer",
            amount: batchSendTotalAmount.toString(),
            symbol: historySymbol,
            txHash: result.txHash,
          });
          await storeHistoryEvent({
            type: "transfer",
            amount: batchSendTotalAmount.toString(),
            symbol: historySymbol,
            txHash: result.txHash,
          });
          if (wallet.spendingKeys && result.recipientNotes?.length) {
            const senderMpkHex = formatMpkHex(wallet.spendingKeys.mpk);
            const senderKey = await hashMpkHex(senderMpkHex);
            const senderInboxPublicKey = formatInboxPublicKeyHex(
              wallet.spendingKeys.spendingKey,
            );

            for (const recipientMeta of result.recipientNotes) {
              const recipientMpkHex =
                recipientMeta.recipientMpkHex.toLowerCase();
              const recipientKey = await hashMpkHex(recipientMpkHex);
              const isSelfRecipient =
                recipientMpkHex === senderMpkHex.toLowerCase();
              const recipientInboxPublicKey =
                recipientMeta.recipientInboxPublicKeyHex ||
                (isSelfRecipient ? senderInboxPublicKey : null);
              if (!recipientInboxPublicKey) {
                addLog(
                  `Recipient inbox public key missing for ${recipientMeta.rawRecipient}`,
                  "warning",
                );
                continue;
              }

              await persistInboxNoteOrQueue({
                label: "Batch recipient",
                note: isSelfRecipient
                  ? recipientMeta.note
                  : {
                      ...recipientMeta.note,
                      privateHistory: {
                        type: "receive" as const,
                        amount: recipientMeta.amountStr,
                        symbol: historySymbol,
                        txHash: result.txHash,
                        timestamp: Date.now(),
                      },
                    },
                recipientKey,
                expectedAuthRecipientKey: isSelfRecipient
                  ? recipientKey
                  : undefined,
                inboxPublicKeyHex: recipientInboxPublicKey,
                txHash: result.txHash,
                keepLocal: isSelfRecipient,
              });

              if (isSelfRecipient) {
                try {
                  const historyAuthHeaders = await authHeadersForPath(
                    "/api/v1/history/store",
                  );
                  await storePrivateHistoryEntry({
                    baseUrl: NOTES_API_URL,
                    headers: historyAuthHeaders,
                    entry: {
                      recipient: recipientKey,
                      type: "receive",
                      amount: recipientMeta.amountStr,
                      token: historySymbol,
                      txHash: result.txHash || "",
                      direction: "in",
                    },
                    encryptTarget: {
                      inboxPublicKeyHex: recipientInboxPublicKey,
                    },
                  });
                } catch (err) {
                  const msg = err instanceof Error ? err.message : String(err);
                  addLog(`Recipient history store failed: ${msg}`, "warning");
                }
              }
            }

            if (result.changeNote) {
              try {
                const commitAuthHeaders = await authHeadersForPath(
                  "/api/v1/commitments",
                );
                await storePrivateCommitment({
                  baseUrl: NOTES_API_URL,
                  addressKey: senderKey,
                  headers: commitAuthHeaders,
                  commitment: {
                    commitment: result.changeNote.commitment,
                    amount: ethers.formatUnits(
                      result.changeNote.value,
                      token.decimals,
                    ),
                    amountWei: result.changeNote.value.toString(),
                    leafIndex: result.changeNote.leafIndex,
                    spent: false,
                    encryptedKeys: {},
                    txHash: result.txHash,
                    timestamp: Math.floor(Date.now() / 1000),
                  },
                  encryptTarget: {
                    inboxPublicKeyHex: senderInboxPublicKey,
                  },
                });
              } catch (err) {
                const msg = err instanceof Error ? err.message : String(err);
                addLog(
                  `Batch transfer change commitment store warning: ${msg}`,
                  "warning",
                );
              }
              await persistInboxNoteOrQueue({
                label: "Batch transfer change",
                note: result.changeNote,
                recipientKey: senderKey,
                expectedAuthRecipientKey: senderKey,
                inboxPublicKeyHex: senderInboxPublicKey,
                txHash: result.txHash,
                keepLocal: true,
              });
            }
          }
          clearSendForm();
        } else if (result.mode === "withdraw_batch") {
          pushHistoryEntry({
            type: "withdraw",
            amount: batchSendTotalAmount.toString(),
            symbol: historySymbol,
            txHash: result.txHash,
          });
          await storeHistoryEvent({
            type: "withdraw",
            amount: batchSendTotalAmount.toString(),
            symbol: historySymbol,
            txHash: result.txHash,
          });
          if (result.changeNote && wallet.spendingKeys) {
            const recipientMpkHex = formatMpkHex(wallet.spendingKeys.mpk);
            const recipientKey = await hashMpkHex(recipientMpkHex);
            const recipientInboxPublicKey = formatInboxPublicKeyHex(
              wallet.spendingKeys.spendingKey,
            );

            try {
              const commitAuthHeaders = await authHeadersForPath(
                "/api/v1/commitments",
              );
              await storePrivateCommitment({
                baseUrl: NOTES_API_URL,
                addressKey: recipientKey,
                headers: commitAuthHeaders,
                commitment: {
                  commitment: result.changeNote.commitment,
                  amount: ethers.formatUnits(
                    result.changeNote.value,
                    token.decimals,
                  ),
                  amountWei: result.changeNote.value.toString(),
                  leafIndex: result.changeNote.leafIndex,
                  spent: false,
                  encryptedKeys: {},
                  txHash: result.txHash,
                  timestamp: Math.floor(Date.now() / 1000),
                },
                encryptTarget: {
                  inboxPublicKeyHex: recipientInboxPublicKey,
                },
              });
            } catch (err) {
              const msg = err instanceof Error ? err.message : String(err);
              addLog(
                `Batch withdraw change commitment store warning: ${msg}`,
                "warning",
              );
            }

            await persistInboxNoteOrQueue({
              label: "Batch withdraw change",
              note: result.changeNote,
              recipientKey,
              inboxPublicKeyHex: recipientInboxPublicKey,
              txHash: result.txHash,
              keepLocal: true,
            });
          }
          clearSendForm();
        }
        return;
      }
      const result = await handleSend(
        sendRecipient,
        amountInput,
        token,
        transferMemo,
      );
      if (
        currentWalletRef.current?.address !== wallet?.address ||
        currentWalletRef.current?.spendingKeys?.mpk !==
          wallet?.spendingKeys?.mpk
      )
        return;
      if (result.migrationNotes?.length) {
        setNotes((previous) =>
          mergeMigrationNotes(
            previous,
            result.migrationNotes!,
            result.spentCommitments,
          ),
        );
      }
      if (
        EVM_CONFIG.JOINSPLIT_V2.ENABLED &&
        result.mode &&
        result.mode !== "withdraw" &&
        result.mode !== "transfer"
      ) {
        await flushFlatSettlementJournal();
      }
      if (result.mode === "transfer") {
        if ((result.spentCommitments?.length ?? 0) > 0) {
          const spentSet = new Set(
            result.spentCommitments!.map((c) => c.toLowerCase()),
          );
          setPendingSpentCommitments((prev) => {
            const next = new Set(prev);
            spentSet.forEach((c) => next.add(c));
            return next;
          });
          setNotes((prev) => {
            const next = prev.map((n) =>
              spentSet.has(n.note.commitment.toLowerCase())
                ? { ...n, spent: true }
                : n,
            );
            if (
              result.changeNote &&
              !next.some(
                (n) =>
                  n.note.commitment.toLowerCase() ===
                  result.changeNote!.commitment.toLowerCase(),
              )
            ) {
              next.push({
                note: result.changeNote,
                timestamp: Date.now(),
                txHash: result.txHash,
              });
            }
            return next;
          });
        }
        const historySymbol = token.isWrappedNft ? "vSTEALTH" : token.symbol;
        pushHistoryEntry({
          type: "transfer",
          amount: amountInput,
          symbol: historySymbol,
          memo: transferMemo || undefined,
          txHash: result.txHash,
        });
        const historyEvent = {
          type: "transfer",
          amount: amountInput,
          symbol: historySymbol,
          memo: transferMemo || undefined,
          txHash: result.txHash,
        };
        if (EVM_CONFIG.JOINSPLIT_V2.ENABLED) {
          // Confirmed outputs already have durable pre-submission recovery.
          // Publish spent/change state above before background delivery/history.
          settledSendPersistence = async () => {
            const results = await Promise.allSettled([
              flushFlatSettlementJournal(),
              storeHistoryEvent(historyEvent),
            ]);
            for (const result of results) {
              if (result.status === "rejected") throw result.reason;
            }
          };
        } else {
          await storeHistoryEvent(historyEvent);
        }
        if (
          !EVM_CONFIG.JOINSPLIT_V2.ENABLED &&
          wallet.spendingKeys &&
          result.recipientNote &&
          result.recipientMpkHex
        ) {
          const senderMpkHex = formatMpkHex(wallet.spendingKeys.mpk);
          const recipientMpkHex = result.recipientMpkHex.toLowerCase();
          const recipientKey = await hashMpkHex(recipientMpkHex);
          const senderInboxPublicKey = formatInboxPublicKeyHex(
            wallet.spendingKeys.spendingKey,
          );
          const isSelfRecipient =
            recipientMpkHex === senderMpkHex.toLowerCase();
          const recipientInboxPublicKey =
            result.recipientInboxPublicKeyHex ||
            (isSelfRecipient ? senderInboxPublicKey : null);

          if (recipientInboxPublicKey) {
            await persistInboxNoteOrQueue({
              label: "Recipient",
              note: isSelfRecipient
                ? result.recipientNote
                : {
                    ...result.recipientNote,
                    privateHistory: {
                      type: "receive" as const,
                      amount: amountInput,
                      symbol: historySymbol,
                      memo: transferMemo || undefined,
                      txHash: result.txHash,
                      timestamp: Date.now(),
                    },
                  },
              recipientKey,
              expectedAuthRecipientKey: isSelfRecipient
                ? recipientKey
                : undefined,
              inboxPublicKeyHex: recipientInboxPublicKey,
              txHash: result.txHash,
              keepLocal: isSelfRecipient,
            });
          } else {
            addLog(
              "Recipient inbox public key missing - receiver note was not persisted to inbox",
              "warning",
            );
          }

          if (isSelfRecipient) {
            try {
              const historyAuthHeaders = await authHeadersForPath(
                "/api/v1/history/store",
              );
              await storePrivateHistoryEntry({
                baseUrl: NOTES_API_URL,
                headers: historyAuthHeaders,
                entry: {
                  recipient: recipientKey,
                  type: "receive",
                  amount: amountInput,
                  token: token.isWrappedNft ? "vSTEALTH" : token.symbol,
                  memo: transferMemo || undefined,
                  txHash: result.txHash || "",
                  direction: "in",
                },
                encryptTarget: {
                  inboxPublicKeyHex: recipientInboxPublicKey || undefined,
                },
              });
            } catch (err) {
              const msg = err instanceof Error ? err.message : String(err);
              addLog(`Recipient history store failed: ${msg}`, "warning");
            }
          }
        }
        if (
          !EVM_CONFIG.JOINSPLIT_V2.ENABLED &&
          result.changeNote &&
          wallet.spendingKeys
        ) {
          const senderMpkHex = formatMpkHex(wallet.spendingKeys.mpk);
          const senderKey = await hashMpkHex(senderMpkHex);
          const senderInboxPublicKey = formatInboxPublicKeyHex(
            wallet.spendingKeys.spendingKey,
          );
          try {
            const commitAuthHeaders = await authHeadersForPath(
              "/api/v1/commitments",
            );
            await storePrivateCommitment({
              baseUrl: NOTES_API_URL,
              addressKey: senderKey,
              headers: commitAuthHeaders,
              commitment: {
                commitment: result.changeNote.commitment,
                amount: ethers.formatUnits(
                  result.changeNote.value,
                  token.decimals,
                ),
                amountWei: result.changeNote.value.toString(),
                leafIndex: result.changeNote.leafIndex,
                spent: false,
                encryptedKeys: {},
                txHash: result.txHash,
                timestamp: Math.floor(Date.now() / 1000),
              },
              encryptTarget: {
                inboxPublicKeyHex: senderInboxPublicKey,
              },
            });
          } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            addLog(
              `Transfer change commitment store warning: ${msg}`,
              "warning",
            );
          }
          await persistInboxNoteOrQueue({
            label: "Transfer change",
            note: result.changeNote,
            recipientKey: senderKey,
            expectedAuthRecipientKey: senderKey,
            inboxPublicKeyHex: senderInboxPublicKey,
            txHash: result.txHash,
            keepLocal: true,
          });
        }
        clearSendForm();
      } else if (result.mode === "migration") {
        const spentSet = new Set(
          (result.spentCommitments || []).map((value) => value.toLowerCase()),
        );
        setPendingSpentCommitments((previous) => {
          const next = new Set(previous);
          spentSet.forEach((commitment) => next.add(commitment));
          return next;
        });
        setNotes((previous) => {
          const next = previous.map((stored) =>
            spentSet.has(stored.note.commitment.toLowerCase())
              ? { ...stored, spent: true }
              : stored,
          );
          if (
            result.changeNote &&
            !next.some(
              (stored) =>
                stored.note.commitment.toLowerCase() ===
                result.changeNote!.commitment.toLowerCase(),
            )
          ) {
            next.push({
              note: result.changeNote,
              timestamp: Date.now(),
              txHash: result.txHash,
            });
          }
          return next;
        });
        if (
          !EVM_CONFIG.JOINSPLIT_V2.ENABLED &&
          result.changeNote &&
          wallet.spendingKeys
        ) {
          const recipientMpkHex = formatMpkHex(wallet.spendingKeys.mpk);
          const recipientKey = await hashMpkHex(recipientMpkHex);
          const recipientInboxPublicKey = formatInboxPublicKeyHex(
            wallet.spendingKeys.spendingKey,
          );
          try {
            const commitAuthHeaders = await authHeadersForPath(
              "/api/v1/commitments",
            );
            await storePrivateCommitment({
              baseUrl: NOTES_API_URL,
              addressKey: recipientKey,
              headers: commitAuthHeaders,
              commitment: {
                commitment: result.changeNote.commitment,
                amount: ethers.formatUnits(
                  result.changeNote.value,
                  token.decimals,
                ),
                amountWei: result.changeNote.value.toString(),
                leafIndex: result.changeNote.leafIndex,
                treeIndex: result.changeNote.treeIndex,
                treeKey: result.changeNote.treeKey,
                spent: false,
                encryptedKeys: {},
                txHash: result.txHash,
                timestamp: Math.floor(Date.now() / 1000),
              },
              encryptTarget: {
                inboxPublicKeyHex: recipientInboxPublicKey,
              },
            });
          } catch (error) {
            const message =
              error instanceof Error ? error.message : String(error);
            addLog(`Migration commitment store warning: ${message}`, "warning");
          }
          await persistInboxNoteOrQueue({
            label: "Tree migration",
            note: result.changeNote,
            recipientKey,
            expectedAuthRecipientKey: recipientKey,
            inboxPublicKeyHex: recipientInboxPublicKey,
            txHash: result.txHash,
            keepLocal: true,
          });
        }
      } else if (result.mode === "withdraw") {
        if ((result.spentCommitments?.length ?? 0) > 0) {
          const spentSet = new Set(
            result.spentCommitments!.map((c) => c.toLowerCase()),
          );
          setPendingSpentCommitments((prev) => {
            const next = new Set(prev);
            spentSet.forEach((c) => next.add(c));
            return next;
          });
          setNotes((prev) => {
            const next = prev.map((n) =>
              spentSet.has(n.note.commitment.toLowerCase())
                ? { ...n, spent: true }
                : n,
            );
            if (
              result.changeNote &&
              !next.some(
                (n) =>
                  n.note.commitment.toLowerCase() ===
                  result.changeNote!.commitment.toLowerCase(),
              )
            ) {
              next.push({
                note: result.changeNote,
                timestamp: Date.now(),
                txHash: result.txHash,
              });
            }
            return next;
          });
        }
        const historySymbol = token.isWrappedNft ? "vSTEALTH" : token.symbol;
        pushHistoryEntry({
          type: "withdraw",
          amount: amountInput,
          symbol: historySymbol,
          txHash: result.txHash,
        });
        const historyEvent = {
          type: "withdraw",
          amount: amountInput,
          symbol: historySymbol,
          txHash: result.txHash,
        };
        if (EVM_CONFIG.JOINSPLIT_V2.ENABLED) {
          // The operation already verified confirmation depth, canonical
          // receipt and durable pre-submission recovery. Update spent/change
          // notes above, then release the UI before scanning other journals.
          settledSendPersistence = async () => {
            const results = await Promise.allSettled([
              flushFlatSettlementJournal(),
              storeHistoryEvent(historyEvent),
            ]);
            for (const result of results) {
              if (result.status === "rejected") throw result.reason;
            }
          };
        } else {
          await storeHistoryEvent(historyEvent);
        }
        // Persist local change note generated by partial withdraw so it
        // survives inbox refresh before Merkle indexing catches up.
        if (
          !EVM_CONFIG.JOINSPLIT_V2.ENABLED &&
          result.changeNote &&
          wallet.spendingKeys
        ) {
          const recipientMpkHex = formatMpkHex(wallet.spendingKeys.mpk);
          const recipientKey = await hashMpkHex(recipientMpkHex);
          const recipientInboxPublicKey = formatInboxPublicKeyHex(
            wallet.spendingKeys.spendingKey,
          );

          try {
            const commitAuthHeaders = await authHeadersForPath(
              "/api/v1/commitments",
            );
            await storePrivateCommitment({
              baseUrl: NOTES_API_URL,
              addressKey: recipientKey,
              headers: commitAuthHeaders,
              commitment: {
                commitment: result.changeNote.commitment,
                amount: ethers.formatUnits(
                  result.changeNote.value,
                  token.decimals,
                ),
                amountWei: result.changeNote.value.toString(),
                leafIndex: result.changeNote.leafIndex,
                spent: false,
                encryptedKeys: {},
                txHash: result.txHash,
                timestamp: Math.floor(Date.now() / 1000),
              },
              encryptTarget: {
                inboxPublicKeyHex: recipientInboxPublicKey,
              },
            });
          } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            addLog(
              `Withdraw change commitment store warning: ${msg}`,
              "warning",
            );
          }

          await persistInboxNoteOrQueue({
            label: "Withdraw change",
            note: result.changeNote,
            recipientKey,
            inboxPublicKeyHex: recipientInboxPublicKey,
            txHash: result.txHash,
            keepLocal: true,
          });
        }
        clearSendForm();
      }
    } finally {
      setLoading(false);
      if (settledSendPersistence) {
        try {
          await settledSendPersistence();
        } catch (error) {
          addLog(
            `Transaction confirmed; recovery or history sync delayed: ${error instanceof Error ? error.message : String(error)}`,
            "warning",
          );
        }
      }
      try {
        await refreshPrivateState();
      } catch (error) {
        addLog(
          `Private state refresh after send delayed: ${error instanceof Error ? error.message : String(error)}`,
          "warning",
        );
      }
    }
  };

  const handleSwapClick = async () => {
    if (!ensureWalletCompliant()) return;
    if (!(await requireMoneyOperationAuth())) return;
    try {
      const result = await handleSwap(
        swapInputToken,
        swapOutputToken,
        swapAmount,
        swapSlippageBps,
        swapQuote,
      );
      if (result.journalId) {
        // V2 swap notes share the same durable, idempotent delivery path as
        // other settlements, including recovery after an interrupted receipt.
        if (result.success) {
          for (const note of result.outputNotes ?? [])
            rememberLocalNote(note, result.depositTxHash);
        }
        void flushFlatSettlementJournal().catch(() =>
          addLog(
            "Your confirmed transaction is saved. Background synchronization will retry.",
            "warning",
          ),
        );
      }
      if (result.success) {
        if (
          result.outputNote &&
          (!isConfirmedTxHash(result.depositTxHash) ||
            !Number.isInteger(result.outputNote.leafIndex) ||
            result.outputNote.leafIndex < 0)
        ) {
          setStatus({
            type: "error",
            message:
              "Swap output is not indexed yet. Refresh private balances before retrying.",
          });
          addLog(
            "Swap output note was not confirmed by the relayer",
            "warning",
          );
          return;
        }

        // Mark spent notes (from the withdraw leg)
        if ((result.spentCommitments?.length ?? 0) > 0) {
          const spentSet = new Set(
            result.spentCommitments!.map((c) => c.toLowerCase()),
          );
          setPendingSpentCommitments((prev) => {
            const next = new Set(prev);
            spentSet.forEach((c) => next.add(c));
            return next;
          });
          setNotes((prev) =>
            prev.map((n) =>
              spentSet.has(n.note.commitment.toLowerCase())
                ? { ...n, spent: true }
                : n,
            ),
          );
        }

        // Store the new output note locally so the user can spend it
        if (result.outputNote) {
          setNotes((prev) => {
            const existing = prev.some(
              (n) =>
                n.note.commitment.toLowerCase() ===
                result.outputNote!.commitment.toLowerCase(),
            );
            if (existing) return prev;
            return [
              ...prev,
              {
                note: result.outputNote!,
                timestamp: Date.now(),
                txHash: result.depositTxHash,
              },
            ];
          });

          // Persist swap output notes to backend inbox/commitment store so they survive sync refresh.
          if (wallet.spendingKeys) {
            const recipientMpkHex = formatMpkHex(wallet.spendingKeys.mpk);
            const recipientKey = await hashMpkHex(recipientMpkHex);
            const recipientInboxPublicKey = formatInboxPublicKeyHex(
              wallet.spendingKeys.spendingKey,
            );

            try {
              const commitAuthHeaders = await authHeadersForPath(
                "/api/v1/commitments",
              );
              await storePrivateCommitment({
                baseUrl: NOTES_API_URL,
                addressKey: recipientKey,
                headers: commitAuthHeaders,
                commitment: {
                  commitment: result.outputNote.commitment,
                  amount: ethers.formatUnits(
                    result.outputNote.value,
                    swapOutputToken.decimals,
                  ),
                  amountWei: result.outputNote.value.toString(),
                  leafIndex: result.outputNote.leafIndex,
                  spent: false,
                  encryptedKeys: {},
                  txHash: result.depositTxHash,
                  timestamp: Math.floor(Date.now() / 1000),
                },
                encryptTarget: {
                  inboxPublicKeyHex: recipientInboxPublicKey,
                },
              });
            } catch (err) {
              const msg = err instanceof Error ? err.message : String(err);
              addLog(`Swap commitment store warning: ${msg}`, "warning");
            }

            await persistInboxNoteOrQueue({
              label: "Swap output",
              note: result.outputNote,
              recipientKey,
              inboxPublicKeyHex: recipientInboxPublicKey,
              txHash: result.depositTxHash,
              keepLocal: true,
            });
          }
        }

        // Persist swap change note (input token remainder) so partial swaps
        // do not lose residual balance visibility while Merkle indexing catches up.
        if (result.changeNote) {
          setNotes((prev) => {
            const existing = prev.some(
              (n) =>
                n.note.commitment.toLowerCase() ===
                result.changeNote!.commitment.toLowerCase(),
            );
            if (existing) return prev;
            return [
              ...prev,
              {
                note: result.changeNote!,
                timestamp: Date.now(),
                txHash: result.depositTxHash,
              },
            ];
          });

          if (wallet.spendingKeys) {
            const recipientMpkHex = formatMpkHex(wallet.spendingKeys.mpk);
            const recipientKey = await hashMpkHex(recipientMpkHex);
            const recipientInboxPublicKey = formatInboxPublicKeyHex(
              wallet.spendingKeys.spendingKey,
            );

            try {
              const commitAuthHeaders = await authHeadersForPath(
                "/api/v1/commitments",
              );
              await storePrivateCommitment({
                baseUrl: NOTES_API_URL,
                addressKey: recipientKey,
                headers: commitAuthHeaders,
                commitment: {
                  commitment: result.changeNote.commitment,
                  amount: ethers.formatUnits(
                    result.changeNote.value,
                    swapInputToken.decimals,
                  ),
                  amountWei: result.changeNote.value.toString(),
                  leafIndex: result.changeNote.leafIndex,
                  spent: false,
                  encryptedKeys: {},
                  txHash: result.depositTxHash,
                  timestamp: Math.floor(Date.now() / 1000),
                },
                encryptTarget: {
                  inboxPublicKeyHex: recipientInboxPublicKey,
                },
              });
            } catch (err) {
              const msg = err instanceof Error ? err.message : String(err);
              addLog(`Swap change commitment store warning: ${msg}`, "warning");
            }

            await persistInboxNoteOrQueue({
              label: "Swap change",
              note: result.changeNote,
              recipientKey,
              inboxPublicKeyHex: recipientInboxPublicKey,
              txHash: result.depositTxHash,
              keepLocal: true,
            });
          }
        }

        const historyType = result.recovered ? "deposit" : "swap";
        const historySymbol = result.recovered
          ? `${swapInputToken.symbol} (recovered)`
          : `${swapInputToken.symbol}->${swapOutputToken.symbol}`;
        pushHistoryEntry({
          type: historyType,
          amount: swapAmount,
          symbol: historySymbol,
          txHash: result.depositTxHash,
        });
        await storeHistoryEvent({
          type: historyType,
          amount: swapAmount,
          symbol: historySymbol,
          txHash: result.depositTxHash,
        });
        clearSwapForm();
      }
    } finally {
      await refreshPrivateState();
    }
  };

  const handleCrossChainClick = async () => {
    if (!ensureWalletCompliant()) return;
    if (!(await requireMoneyOperationAuth())) return;
    if (!wallet.connected || !wallet.spendingKeys || !wallet.address) {
      setStatus({
        type: "error",
        message: "Connect wallet and derive keys first",
      });
      return;
    }
    if (!crossChainQuoteRequestKey) {
      setStatus({
        type: "error",
        message: crossChainQuoteError || "No bridge quote inputs available yet",
      });
      return;
    }
    if (crossChainDestinationResolutionLoading) {
      setStatus({
        type: "error",
        message: "Destination recipient is still resolving",
      });
      return;
    }
    if (crossChainRecipientMode !== "public") {
      setStatus({
        type: "error",
        message: "Bridge v1 supports public destination recipients only",
      });
      return;
    }
    if (!crossChainPublicRecipientAddress) {
      setStatus({
        type: "error",
        message:
          crossChainDestinationResolutionError ||
          "Destination public recipient address is invalid",
      });
      return;
    }
    try {
      const sourceAmountBase = ethers
        .parseUnits(crossChainAmount || "0", crossChainInputToken.decimals)
        .toString();
      if (
        crossChainSourceAmountBase === null ||
        crossChainSourceAmountBase <= 0n
      ) {
        setStatus({
          type: "error",
          message: "Bridge amount must be greater than zero",
        });
        return;
      }

      const fetchFreshBridgeQuote = async (attempt: number) => {
        setCrossChainQuoteLoading(true);
        setCrossChainQuoteError(null);
        setStatus({
          type: "info",
          message:
            attempt === 0
              ? "Refreshing bridge quote..."
              : "DLN rejected the quote. Refreshing and retrying once...",
        });
        try {
          const quote = await submitBridgeQuote({
            sourceChainId: EVM_CONFIG.CHAIN_ID,
            sourceToken: crossChainInputToken.address,
            sourceAmount: sourceAmountBase,
            sourceAmountIsNet: false,
            destinationChainId: BRIDGE_DESTINATION_CHAIN_ID,
            destinationToken: crossChainDestinationTokenAddress,
            destinationRecipient: crossChainExecutionDestinationRecipient,
            sourceAuthority: wallet.address,
            destinationAuthority: crossChainExecutionDestinationAuthority,
            prependOperatingExpenses: false,
            allowPartialFill: false,
            idempotencyKey: buildBridgeQuoteIdempotencyKey(
              `bridge-execute-${attempt}`,
              attempt,
            ),
          });
          const quoteWithMetadata = withBridgeQuoteMetadata(
            quote,
            crossChainQuoteRequestKey,
            wallet.address,
          );
          setCrossChainQuote(quoteWithMetadata);
          return quoteWithMetadata;
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          setCrossChainQuote(null);
          setCrossChainQuoteError(msg);
          throw new Error(msg);
        } finally {
          setCrossChainQuoteLoading(false);
        }
      };

      let quoteForExecution = await fetchFreshBridgeQuote(0);
      let result = await handleBridge({
        chainId: EVM_CONFIG.CHAIN_ID,
        sourceToken: crossChainInputToken.address,
        sourceSpendAmount: sourceAmountBase,
        sourceTokenDecimals: crossChainInputToken.decimals,
        quote: quoteForExecution,
        onOrderUpdate: (statusUpdate) => setCrossChainOrderStatus(statusUpdate),
      });
      if (
        !result?.success &&
        !result?.pending &&
        !result?.journalId &&
        !result?.transactionHash &&
        isDlnQuoteNotAcceptedError(result?.error || null)
      ) {
        addLog(
          "DLN rejected the bridge quote; retrying with a fresh quote.",
          "warning",
        );
        quoteForExecution = await fetchFreshBridgeQuote(1);
        result = await handleBridge({
          chainId: EVM_CONFIG.CHAIN_ID,
          sourceToken: crossChainInputToken.address,
          sourceSpendAmount: sourceAmountBase,
          sourceTokenDecimals: crossChainInputToken.decimals,
          quote: quoteForExecution,
          onOrderUpdate: (statusUpdate) =>
            setCrossChainOrderStatus(statusUpdate),
        });
      }
      if (!result?.success) return;
      if ((result?.spentCommitments?.length ?? 0) > 0) {
        const spentSet = new Set(
          result.spentCommitments!.map((c) => c.toLowerCase()),
        );
        setPendingSpentCommitments((prev) => {
          const next = new Set(prev);
          spentSet.forEach((c) => next.add(c));
          return next;
        });
        setNotes((prev) => {
          const next = prev.map((n) =>
            spentSet.has(n.note.commitment.toLowerCase())
              ? { ...n, spent: true }
              : n,
          );
          if (
            result.changeNote &&
            !next.some(
              (n) =>
                n.note.commitment.toLowerCase() ===
                result.changeNote!.commitment.toLowerCase(),
            )
          ) {
            next.push({
              note: result.changeNote,
              timestamp: Date.now(),
              txHash: result.transactionHash,
            });
          }
          return next;
        });
      }
      if (result?.changeNote && wallet.spendingKeys) {
        const recipientMpkHex = formatMpkHex(wallet.spendingKeys.mpk);
        const recipientKey = await hashMpkHex(recipientMpkHex);
        const recipientInboxPublicKey = formatInboxPublicKeyHex(
          wallet.spendingKeys.spendingKey,
        );
        if (
          Number.isInteger(result.changeNote.leafIndex) &&
          result.changeNote.leafIndex >= 0
        ) {
          try {
            const commitAuthHeaders = await authHeadersForPath(
              "/api/v1/commitments",
            );
            await storePrivateCommitment({
              baseUrl: NOTES_API_URL,
              addressKey: recipientKey,
              headers: commitAuthHeaders,
              commitment: {
                commitment: result.changeNote.commitment,
                amount: ethers.formatUnits(
                  result.changeNote.value,
                  crossChainInputToken.decimals,
                ),
                amountWei: result.changeNote.value.toString(),
                leafIndex: result.changeNote.leafIndex,
                spent: false,
                encryptedKeys: {},
                txHash: result.transactionHash,
                timestamp: Math.floor(Date.now() / 1000),
              },
              encryptTarget: {
                inboxPublicKeyHex: recipientInboxPublicKey,
              },
            });
          } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            addLog(
              `Bridge source change commitment store warning: ${msg}`,
              "warning",
            );
          }
        } else {
          addLog(
            "Bridge change note leaf index pending; skipping commitment store until sync.",
            "info",
          );
        }
        await persistInboxNoteOrQueue({
          label: "Bridge source change",
          note: result.changeNote,
          recipientKey,
          inboxPublicKeyHex: recipientInboxPublicKey,
          txHash: result.transactionHash,
          keepLocal: true,
        });
      }
      if (result?.orderId) {
        addLog(`Bridge order tracking: ${result.orderId}`, "info");
        pushHistoryEntry({
          type: "withdraw",
          amount: crossChainAmount,
          symbol: `${crossChainInputToken.symbol} -> ${crossChainDestinationChain.toUpperCase()}`,
          txHash: result.transactionHash,
        });
        await storeHistoryEvent({
          type: "withdraw",
          amount: crossChainAmount,
          symbol: `${crossChainInputToken.symbol}->${crossChainDestinationChain.toUpperCase()}`,
          txHash: result.transactionHash,
          memo: result.orderId,
        });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      addLog(`Cross-chain bridge failed: ${msg}`, "error");
      setStatus({ type: "error", message: msg });
    }
  };

  const handleCrossChainCancel = async () => {
    const orderId =
      crossChainOrderStatus?.order_id ||
      crossChainQuote?.order_id ||
      crossChainQuote?.orderId;
    const sourceAuthority =
      crossChainOrderStatus?.source_authority ||
      crossChainOrderStatus?.sourceAuthority ||
      crossChainQuote?.source_authority ||
      crossChainQuote?.sourceAuthority ||
      wallet.address;
    if (!orderId) {
      setStatus({ type: "error", message: "No active bridge order to cancel" });
      return;
    }
    const result = await handleBridgeCancel(orderId, sourceAuthority);
    if (result.success) {
      try {
        const refreshed = await (
          await import("../../utils/relayer-client")
        ).getBridgeStatus(orderId, sourceAuthority);
        setCrossChainOrderStatus(refreshed);
      } catch {
        // best effort refresh
      }
    }
  };

  const copyPaymentLink = async () => {
    if (!wallet.spendingKeys) {
      setStatus({ type: "error", message: "Connect wallet first" });
      return;
    }
    const mpkHex =
      "0x" + wallet.spendingKeys.mpk.toString(16).padStart(64, "0");
    const inboxPubKey = formatInboxPublicKeyHex(
      wallet.spendingKeys.spendingKey,
    );
    const url = new URL(window.location.href);
    url.pathname = `${EVM_CONFIG.NETWORK?.route ?? "/base"}/send`;
    url.searchParams.set("vault", `${mpkHex}:${inboxPubKey}`);
    url.searchParams.set("chainId", String(chainId));
    const paymentLinkAmount =
      paymentLinkToken?.tokenType === "erc721"
        ? "1"
        : paymentLinkAmountInput.trim();
    if (paymentLinkAmount) {
      url.searchParams.set("amount", paymentLinkAmount);
    }
    if (paymentLinkToken) {
      url.searchParams.set("token", paymentLinkToken.address);
      url.searchParams.delete("anyToken");
    } else {
      url.searchParams.delete("token");
      url.searchParams.set("anyToken", "1");
    }
    await navigator.clipboard.writeText(url.toString());
    addLog("Payment link copied", "success");
    setStatus({ type: "success", message: "Payment link copied" });
  };

  const copyPrivateAddress = () => {
    if (!wallet.spendingKeys) {
      setStatus({ type: "error", message: "Connect wallet first" });
      return;
    }
    const privateAddress = `${formatMpkHex(wallet.spendingKeys.mpk)}:${formatInboxPublicKeyHex(wallet.spendingKeys.spendingKey)}`;
    void navigator.clipboard.writeText(privateAddress);
    addLog("Private address copied to clipboard", "success");
    setStatus({ type: "success", message: "Private P2P address copied" });
  };

  const handleWalletButton = () => {
    if (wallet.connected) {
      clearProxyAuthToken();
      disconnectWallet();
    } else {
      setWalletModalOpen(true);
    }
  };

  const isComplianceBlocked =
    enableRangeScreening &&
    (screeningStatus === "blocked" || screeningStatus === "error");
  const isComplianceChecking =
    enableRangeScreening && screeningStatus === "checking";

  return {
    onchainRecoveryComplete:
      chainRecovery?.scope === walletSyncScope &&
      chainRecovery?.state === "ready",
    onchainRecoveryFailed:
      chainRecovery?.scope === walletSyncScope &&
      chainRecovery?.state === "error",
    prepareBridgeAction: async () =>
      ensureWalletCompliant() && (await requireMoneyOperationAuth()),
    refreshPrivateState,
    handleBalancesRefresh,
    handleHistoryRefresh,
    recipientParsed,
    recipientHexMpk,
    isPrivateRecipient,
    isSendNftToken,
    sendFeesApply,
    privateFeePolicy,
    protocolFeeBps,
    partnerFeeBps,
    canUseBatchWithdraw,
    parsedSendAmount,
    sendProtocolFee,
    sendPartnerFee,
    batchSendTotalAmount,
    batchSendProtocolFee,
    batchSendPartnerFee,
    clearSendForm,
    handleBatchFileUpload,
    enterBatchMode,
    clearSwapForm,
    ensureWalletCompliant,
    ensureShieldDepositReady,
    fundPerpsVaultFromPrivateAsset,
    shieldPerpsWithdrawAssetToPrivate,
    ingestPerpsWithdrawPrivateNote,
    complianceToast,
    perps,
    handleShieldClick,
    handleSendClick,
    handleSwapClick,
    handleCrossChainClick,
    handleCrossChainCancel,
    copyPaymentLink,
    copyPrivateAddress,
    handleWalletButton,
    isComplianceBlocked,
    isComplianceChecking,
  };
}
